import axios from 'axios';

const API_BASE = '/api/trainings';

function addAuthInterceptors(instance: ReturnType<typeof axios.create>) {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('grant_crm_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('grant_crm_token');
        localStorage.removeItem('grant_crm_user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

const api = addAuthInterceptors(
  axios.create({ baseURL: API_BASE, timeout: 30000, headers: { 'Content-Type': 'application/json' } })
);

// ─── Types ─────────────────────────────────────────────────────────

export interface TrainingCompany {
  id: number;
  name: string;
  short_name: string;
  nip: string;
  logo_url: string | null;
  is_active: boolean;
  contact_email: string | null;
  contact_phone: string | null;
  address_city: string | null;
}

export interface TrainingCourse {
  id: number;
  code: string;
  name: string;
  category: string | null;
  description: string | null;
  duration_hours: number | null;
  min_participants: number;
  max_participants: number;
  certification_type: 'zaświadczenie' | 'certyfikat' | 'brak';
  bur_service_id: string | null;
  is_active: boolean;
  kfs_zakres: string | null;
}

export interface TrainingTrainer {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  specializations: string[];
  company_id: number | null;
  company_name: string | null;
  team_member_id: number | null;
  team_member_name: string | null;
  team_member_position: string | null;
  is_active: boolean;
  // Extended profile
  title: string | null;
  bio: string | null;
  bio_long: string | null;
  photo_url: string | null;
  photo_gdrive_id: string | null;
  gdrive_folder_id: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  experience_years: number | null;
  education: string | null;
  certifications: string[];
  compensation_model: 'per_day' | 'per_person' | 'per_training' | 'mixed' | null;
  rate_half_day: number | null;
  rate_full_day: number | null;
  rate_per_person: number | null;
  hourly_rate: number | null;
  onboarding_status: string | null;
  onboarding_notes: string | null;
}

export interface TrainerDocument {
  id: number;
  trainer_id: number;
  category: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  gdrive_file_id: string | null;
  gdrive_link: string | null;
  description: string | null;
  session_id: number | null;
  created_at: string;
}

export interface TrainerSessionRate {
  id: number;
  trainer_id: number;
  session_id: number;
  rate_type: string;
  rate_amount: number;
  notes: string | null;
  session_code: string;
  course_name: string;
  start_date?: string;
  end_date?: string;
}

export interface AvailableTeamMember {
  id: number;
  full_name: string;
  email: string | null;
  position: string | null;
  department: string | null;
}

export interface TrainingParticipant {
  id: number;
  first_name: string;
  last_name: string;
  pesel: string | null;
  email: string | null;
  phone: string | null;
  employer_name: string | null;
  employer_nip: string | null;
  position: string | null;
  education_level: string | null;
  created_at: string;
}

export type SessionStatus = 'planowana' | 'rekrutacja' | 'potwierdźona' | 'w_trakcie' | 'zakończona' | 'anulowana';
export type FundingSource = 'BUR' | 'KFS' | 'KOMERCYJNE' | 'EFS' | 'INNE';
export type EnrollmentStatus = 'zapisany' | 'potwierdźony' | 'obecny' | 'ukończony' | 'certyfikowany' | 'zrezygnowany' | 'nieobecny';

export interface TrainingSession {
  id: number;
  course_id: number;
  company_id: number;
  trainer_id: number | null;
  session_code: string;
  start_date: string;
  end_date: string;
  schedule_details: string | null;
  location: string | null;
  location_type: 'stacjonarne' | 'online' | 'hybrydowe';
  status: SessionStatus;
  max_participants: number | null;
  total_hours: number | null;
  price_per_person: number | null;
  notes: string | null;
  // JOINed
  course_code: string;
  course_name: string;
  course_category: string | null;
  company_name: string;
  trainer_name: string | null;
  enrollment_count: number;
  dominant_funding: FundingSource | null;
}

export interface SessionDetail extends TrainingSession {
  course_description: string | null;
  duration_hours: number | null;
  certification_type: string;
  company_short_name: string;
  company_nip: string;
  trainer_email: string | null;
  trainer_phone: string | null;
}

export interface TrainingEnrollment {
  id: number;
  session_id: number;
  participant_id: number | null;
  crm_application_id: number | null;
  funding_source: FundingSource;
  funding_amount: number | null;
  own_contribution: number;
  employer_name: string | null;
  employer_nip: string | null;
  status: EnrollmentStatus;
  attendance_percent: number | null;
  grade: string | null;
  certificate_number: string | null;
  certificate_issued_at: string | null;
  notes: string | null;
  metadata: Record<string, any>;
  // JOINed
  participant_first_name: string | null;
  participant_last_name: string | null;
  participant_email: string | null;
  participant_phone: string | null;
  participant_pesel: string | null;
  participant_employer: string | null;
  crm_company_name: string | null;
  crm_contact_person: string | null;
}

export interface AttendanceRecord {
  id?: number;
  enrollment_id: number;
  attendance_date: string;
  present: boolean;
  hours_attended: number | null;
  notes: string | null;
}

export interface TrainingStats {
  totals: {
    total_sessions: number;
    active_sessions: number;
    completed_sessions: number;
    total_enrollments: number;
    total_courses: number;
    total_trainers: number;
    total_participants: number;
  };
  sessionsByStatus: { status: string; count: number }[];
  enrollmentsByFunding: { funding_source: string; count: number; total_funding: number }[];
  enrollmentsByStatus: { status: string; count: number }[];
  byCompany: { id: number; short_name: string; name: string; sessions_count: number; enrollments_count: number }[];
  topTrainers: { id: number; first_name: string; last_name: string; specializations: string[] | null; session_count: number; total_students: number }[];
  monthlyTrend: { month: string; sessions: number; enrollments: number }[];
}

export interface TrainingCertificate {
  id: number;
  enrollment_id: number;
  session_id: number;
  company_id: number;
  certificate_type: 'zaświadczenie' | 'certyfikat';
  certificate_number: string;
  issued_at: string;
  qr_token: string;
  participant_name: string;
  participant_email: string | null;
}

export interface CertificateResult {
  enrollment_id: number;
  certificate_number: string;
  certificate_id: number;
  qr_token: string;
}

export type RegistrationStatus = 'oczekująca' | 'potwierdźona' | 'opłacona' | 'zapisana' | 'anulowana';
export type PaymentStatus = 'nieopłacone' | 'proforma_wysłana' | 'opłacone' | 'zwrócone';
export type PaymentPath = 'samodzielna' | 'dofinansowanie_bur' | 'finansowanie_kfs';

export interface TrainingRegistration {
  id: number;
  session_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  pesel: string | null;
  employer_name: string | null;
  employer_nip: string | null;
  payment_path: PaymentPath;
  bur_support_id: string | null;
  status: RegistrationStatus;
  payment_status: PaymentStatus;
  amount_total: number | null;
  amount_funded: number | null;
  amount_own: number | null;
  consent_rodo: boolean;
  consent_marketing: boolean;
  source: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BurImportResult {
  format: 'participants' | 'services' | 'unknown';
  created: number;
  updated: number;
  enrollmentsCreated: number;
  errors: string[];
  total: number;
  message: string;
}

export interface BurImportPreview {
  format: string;
  headers: string[];
  rows: string[][];
  columnMap: Record<string, number>;
  total: number;
}

// ─── Moduł 1: Split Invoices ────────────────────────────────────────

export type InvoiceStatus = 'oczekująca' | 'wystawiona' | 'wysłana' | 'opłacona' | 'anulowana';

export interface SplitInvoice {
  id: number;
  enrollment_id: number;
  session_id: number;
  company_id: number;
  total_gross: number;
  tax_rate: number;
  funding_percent: number;
  funding_amount: number;
  funding_invoice_number: string | null;
  funding_invoice_id: number | null;
  funding_invoice_status: InvoiceStatus;
  funding_buyer_name: string | null;
  funding_buyer_nip: string | null;
  own_percent: number;
  own_amount: number;
  own_invoice_number: string | null;
  own_invoice_id: number | null;
  own_invoice_status: InvoiceStatus;
  own_buyer_name: string | null;
  own_buyer_nip: string | null;
  bur_support_id: string | null;
  bur_service_id: string | null;
  notes: string | null;
  created_at: string;
  participant_name?: string;
  course_name?: string;
  session_code?: string;
}

export interface SplitCalculation {
  fundingPercent: number;
  fundingAmount: number;
  fundingNet: number;
  ownPercent: number;
  ownAmount: number;
  ownNet: number;
  totalNet: number;
  taxRate: number;
}

// ─── Moduł 2: Resources ────────────────────────────────────────────

export type ResourceType = 'sala' | 'catering' | 'sprzęt' | 'inne';

export interface TrainingResource {
  id: number;
  company_id: number | null;
  resource_type: ResourceType;
  name: string;
  description: string | null;
  capacity: number | null;
  location: string | null;
  hourly_rate: number | null;
  daily_rate: number | null;
  contact_person: string | null;
  contact_phone: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ConflictInfo {
  resourceType: 'trener' | 'sala';
  resourceName: string;
  conflictDate: string;
  conflictingSessionCode: string;
  conflictingSessionId: number;
}

export interface AvailabilityResult {
  available: boolean;
  conflicts: ConflictInfo[];
}

// ─── Moduł 3: Tests & BUR Compliance ───────────────────────────────

export type TestType = 'test_wstępny' | 'test_końcowy';

export interface TrainingTest {
  id: number;
  course_id: number;
  test_type: TestType;
  title: string;
  description: string | null;
  questions: TestQuestion[];
  passing_score: number | null;
  time_limit_minutes: number | null;
  is_active: boolean;
  created_at: string;
}

export interface TestQuestion {
  id: string;
  text: string;
  type: 'single_choice' | 'multiple_choice' | 'open' | 'scale';
  options?: string[];
  correct_answer?: string | string[];
  points?: number;
}

export interface TestResponse {
  id: number;
  test_id: number;
  enrollment_id: number;
  answers: Record<string, any>;
  score: number | null;
  passed: boolean | null;
  completed_at: string | null;
  participant_name?: string;
}

export interface BurComplianceResult {
  compliant: boolean;
  missing: string[];
  warnings: string[];
}

export interface BurStatusResponse {
  connected: boolean;
  lastSync: string | null;
  providers: number;
  services: number;
  error?: string;
}

export interface BurSyncResponse {
  message: string;
  duration: number;
  logId: number;
  providers: number;
  services: number;
  schedules: number;
  trainers: number;
}

export interface EntityEmailConfig {
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_from: string | null;
  smtp_from_name: string | null;
  configured: boolean;
}

export interface SessionsFilters {
  companyId?: number | null;
  status?: SessionStatus;
  courseId?: number;
  trainerId?: number;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  pagination: { total: number; page: number; limit: number; totalPages: number };
  sessions: T[];
  items?: T[];
}

// ─── API Methods ────────────────────────────────────────────────────

export const trainingsApi = {
  // Companies
  async getCompanies(): Promise<TrainingCompany[]> {
    const { data } = await api.get('/companies', { params: { active: 'true' } });
    return data.companies;
  },

  async getCompany(id: number): Promise<TrainingCompany> {
    const { data } = await api.get(`/companies/${id}`);
    return data.company;
  },

  // Courses
  async getCourses(): Promise<TrainingCourse[]> {
    const { data } = await api.get('/courses', { params: { active: 'true' } });
    return data.courses;
  },

  // Trainers
  async getTrainers(companyId?: number): Promise<TrainingTrainer[]> {
    const params: Record<string, any> = { active: 'true' };
    if (companyId) params.companyId = companyId;
    const { data } = await api.get('/trainers', { params });
    return data.trainers;
  },

  // Sessions
  async getSessions(filters: SessionsFilters = {}): Promise<PaginatedResponse<TrainingSession>> {
    const params: Record<string, any> = {};
    if (filters.companyId) params.companyId = filters.companyId;
    if (filters.status) params.status = filters.status;
    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.trainerId) params.trainerId = filters.trainerId;
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    const { data } = await api.get('/sessions', { params });
    return data;
  },

  async getSession(id: number): Promise<{ session: SessionDetail; enrollments: TrainingEnrollment[] }> {
    const { data } = await api.get(`/sessions/${id}`);
    return data;
  },

  async updateSessionStatus(id: number, status: SessionStatus): Promise<void> {
    await api.patch(`/sessions/${id}/status`, { status });
  },

  // Enrollments
  async getEnrollments(sessionId: number): Promise<TrainingEnrollment[]> {
    const { data } = await api.get(`/sessions/${sessionId}/enrollments`);
    return data.enrollments;
  },

  async createEnrollment(sessionId: number, enrollment: {
    participantId?: number;
    crmApplicationId?: number;
    fundingSource?: FundingSource;
    fundingAmount?: number;
    ownContribution?: number;
    employerName?: string;
    employerNip?: string;
    notes?: string;
  }): Promise<{ id: number }> {
    const { data } = await api.post(`/sessions/${sessionId}/enrollments`, enrollment);
    return data;
  },

  async updateEnrollment(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/enrollments/${id}`, updates);
  },

  async deleteEnrollment(id: number): Promise<void> {
    await api.delete(`/enrollments/${id}`);
  },

  // Attendance
  async getAttendance(enrollmentId: number): Promise<AttendanceRecord[]> {
    const { data } = await api.get(`/enrollments/${enrollmentId}/attendance`);
    return data.attendance;
  },

  async saveAttendance(enrollmentId: number, records: Omit<AttendanceRecord, 'id' | 'enrollment_id'>[]): Promise<void> {
    await api.put(`/enrollments/${enrollmentId}/attendance`, { records });
  },

  // Google Calendar sync
  async syncSessionToCalendar(sessionId: number): Promise<{ eventId: string; htmlLink: string }> {
    const { data } = await api.post(`/sessions/${sessionId}/gcal-sync`);
    return data;
  },

  async syncAllSessionsToCalendar(): Promise<{ synced: number; errors: number; total: number }> {
    const { data } = await api.post('/sessions/gcal-sync-all');
    return data;
  },

  // Surveys
  async generateSurveyLinks(sessionId: number): Promise<{ links: any[]; count: number }> {
    const { data } = await api.post(`/sessions/${sessionId}/surveys/generate`);
    return data;
  },

  async getSessionSurveys(sessionId: number): Promise<any> {
    const { data } = await api.get(`/sessions/${sessionId}/surveys`);
    return data;
  },

  async getGlobalSurveyStats(): Promise<any> {
    const { data } = await api.get('/surveys/stats');
    return data;
  },

  // Stats
  async getStats(companyId?: number | null): Promise<TrainingStats> {
    const params: Record<string, any> = {};
    if (companyId) params.companyId = companyId;
    const { data } = await api.get('/stats', { params });
    return data;
  },

  // BUR export
  async exportBurCsv(courseId: number): Promise<Blob> {
    const { data } = await api.get(`/bur/export/${courseId}`, {
      params: { format: 'csv' },
      responseType: 'blob',
    });
    return data;
  },

  async getBurStatus(): Promise<BurStatusResponse> {
    const { data } = await api.get('/bur/status');
    return data;
  },

  // Course CRUD
  async createCourse(course: {
    code: string; name: string; category?: string | null; description?: string | null;
    durationHours?: number | null; minParticipants?: number; maxParticipants?: number;
    certificationType?: string; burServiceId?: string | null;
  }): Promise<{ id: number }> {
    const { data } = await api.post('/courses', course);
    return data;
  },

  async updateCourse(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/courses/${id}`, updates);
  },

  // Team members available for trainer assignment
  async getAvailableTeamMembers(): Promise<AvailableTeamMember[]> {
    const { data } = await api.get('/team-members-available');
    return data.members;
  },

  // Trainer CRUD
  async createTrainer(trainer: {
    firstName: string; lastName: string; email?: string | null; phone?: string | null;
    specializations?: string[]; bio?: string | null; hourlyRate?: number | null;
    companyId?: number | null; teamMemberId?: number | null;
    title?: string | null; bioLong?: string | null; linkedinUrl?: string | null;
    websiteUrl?: string | null; experienceYears?: number | null; education?: string | null;
    certifications?: string[]; compensationModel?: string | null;
    rateHalfDay?: number | null; rateFullDay?: number | null; ratePerPerson?: number | null;
  }): Promise<{ id: number }> {
    const { data } = await api.post('/trainers', trainer);
    return data;
  },

  async updateTrainer(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/trainers/${id}`, updates);
  },

  // Trainer photo upload
  async uploadTrainerPhoto(id: number, file: File): Promise<{ photoUrl: string; fileId: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    const { data } = await api.post(`/trainers/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // Trainer documents
  async getTrainerDocuments(id: number, category?: string): Promise<{ documents: TrainerDocument[]; categories: Record<string, string> }> {
    const params: Record<string, any> = {};
    if (category) params.category = category;
    const { data } = await api.get(`/trainers/${id}/documents`, { params });
    return data;
  },

  async uploadTrainerDocument(id: number, file: File, category: string, description?: string, sessionId?: number): Promise<{ id: number; gdriveLink: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (description) formData.append('description', description);
    if (sessionId) formData.append('sessionId', String(sessionId));
    const { data } = await api.post(`/trainers/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async deleteTrainerDocument(trainerId: number, docId: number): Promise<void> {
    await api.delete(`/trainers/${trainerId}/documents/${docId}`);
  },

  // Trainer GDrive folder
  async createTrainerGdriveFolder(id: number): Promise<{ folderId: string }> {
    const { data } = await api.post(`/trainers/${id}/gdrive-folder`);
    return data;
  },

  // Trainer session rates
  async getTrainerSessionRates(id: number): Promise<{ rates: TrainerSessionRate[] }> {
    const { data } = await api.get(`/trainers/${id}/session-rates`);
    return data;
  },

  async setTrainerSessionRate(trainerId: number, sessionId: number, rateType: string, rateAmount: number, notes?: string): Promise<{ id: number }> {
    const { data } = await api.post(`/trainers/${trainerId}/session-rates`, { sessionId, rateType, rateAmount, notes });
    return data;
  },

  async deleteTrainerSessionRate(trainerId: number, rateId: number): Promise<void> {
    await api.delete(`/trainers/${trainerId}/session-rates/${rateId}`);
  },

  // Participant CRUD
  async getParticipants(filters: { search?: string; page?: number; limit?: number } = {}): Promise<{
    participants: TrainingParticipant[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const { data } = await api.get('/participants', { params: filters });
    return data;
  },

  async createParticipant(p: {
    firstName: string; lastName: string; pesel?: string | null; email?: string | null;
    phone?: string | null; employerName?: string | null; employerNip?: string | null;
    position?: string | null; educationLevel?: string | null;
  }): Promise<{ id: number }> {
    const { data } = await api.post('/participants', p);
    return data;
  },

  async updateParticipant(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/participants/${id}`, updates);
  },

  // Session create/update
  async createSession(session: {
    courseId: number; companyId: number; trainerId?: number | null;
    startDate: string; endDate: string; location?: string | null;
    locationType?: string; maxParticipants?: number | null;
    totalHours?: number | null; pricePerPerson?: number | null; notes?: string | null;
  }): Promise<{ id: number; sessionCode: string }> {
    const { data } = await api.post('/sessions', session);
    return data;
  },

  async updateSession(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/sessions/${id}`, updates);
  },

  // Company CRUD
  async createCompany(company: {
    name: string; shortName: string; nip?: string | null;
    contactEmail?: string | null; contactPhone?: string | null;
    address?: string | null; addressCity?: string | null; website?: string | null;
  }): Promise<{ id: number }> {
    const { data } = await api.post('/companies', company);
    return data;
  },

  async updateCompany(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/companies/${id}`, updates);
  },

  // Course detail (with sessions)
  async getCourse(id: number): Promise<{ course: TrainingCourse; sessions: TrainingSession[] }> {
    const { data } = await api.get(`/courses/${id}`);
    return data;
  },

  // Trainer detail (with sessions)
  async getTrainer(id: number): Promise<{
    trainer: TrainingTrainer; sessions: TrainingSession[];
    documents: TrainerDocument[]; sessionRates: TrainerSessionRate[];
    documentCategories: Record<string, string>;
  }> {
    const { data } = await api.get(`/trainers/${id}`);
    return data;
  },

  // BUR sync
  async triggerBurSync(courseId?: number): Promise<BurSyncResponse> {
    const { data } = await api.post('/bur/sync', { courseId });
    return data;
  },

  // ─── Certificates ─────────────────────────────────────────────

  async generateCertificates(sessionId: number, certType: 'zaświadczenie' | 'certyfikat' = 'zaświadczenie'): Promise<{
    message: string; generated: number; certificates: CertificateResult[];
  }> {
    const { data } = await api.post(`/sessions/${sessionId}/certificates/generate`, { certType });
    return data;
  },

  async getSessionCertificates(sessionId: number): Promise<TrainingCertificate[]> {
    const { data } = await api.get(`/sessions/${sessionId}/certificates`);
    return data.certificates;
  },

  getCertificatePdfUrl(certId: number): string {
    return `${API_BASE}/certificates/${certId}/pdf`;
  },

  async downloadCertificatePdf(certId: number): Promise<Blob> {
    const { data } = await api.get(`/certificates/${certId}/pdf`, { responseType: 'blob' });
    return data;
  },

  async downloadSessionCertificatesZip(sessionId: number): Promise<Blob> {
    const { data } = await api.get(`/sessions/${sessionId}/certificates/zip`, { responseType: 'blob', timeout: 120000 });
    return data;
  },

  // ─── PDF Documents (attendance + evaluation) ──────────────────

  async downloadAttendanceList(sessionId: number): Promise<Blob> {
    const { data } = await api.post(`/sessions/${sessionId}/attendance-list`, {}, { responseType: 'blob' });
    return data;
  },

  async downloadEvaluationForm(sessionId: number): Promise<Blob> {
    const { data } = await api.post(`/sessions/${sessionId}/evaluation-form`, {}, { responseType: 'blob' });
    return data;
  },

  async generateBurPackage(sessionId: number): Promise<{
    message: string;
    attendance: { ready: boolean; size: number };
    evaluation: { ready: boolean; size: number };
    certificates: { generated: number; items: { id: number; number: string }[] };
  }> {
    const { data } = await api.post(`/sessions/${sessionId}/bur-package`);
    return data;
  },

  // ─── BUR File Import ──────────────────────────────────────────

  async previewBurImport(file: File): Promise<BurImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/bur/import-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async importBurFile(file: File, sessionId: number): Promise<BurImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', String(sessionId));
    const { data } = await api.post('/bur/import-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async importBurServices(file: File, companyId: number): Promise<BurImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', String(companyId));
    const { data } = await api.post('/bur/import-services', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  // ─── Registrations (public online registrations management) ───

  async getSessionRegistrations(sessionId: number): Promise<TrainingRegistration[]> {
    const { data } = await api.get(`/sessions/${sessionId}/registrations`);
    return data.registrations;
  },

  async updateRegistration(id: number, updates: { status?: RegistrationStatus; paymentStatus?: PaymentStatus; notes?: string }): Promise<void> {
    await api.patch(`/registrations/${id}`, updates);
  },

  async confirmRegistration(id: number): Promise<{ participantId: number; enrollmentId: number | null }> {
    const { data } = await api.post(`/registrations/${id}/confirm`);
    return data;
  },

  // ─── Moduł 1: Split Invoices (BUR) ────────────────────────────────

  async createSplitInvoice(config: {
    enrollmentId: number; totalGross: number; taxRate?: number; fundingPercent?: number;
    fundingBuyerName: string; fundingBuyerNip: string;
    ownBuyerName?: string; ownBuyerNip?: string;
    burSupportId?: string; burServiceId?: string; notes?: string;
  }): Promise<{ message: string; invoice: SplitInvoice }> {
    const { data } = await api.post('/invoices/split', config);
    return data;
  },

  async getSessionInvoices(sessionId: number): Promise<SplitInvoice[]> {
    const { data } = await api.get(`/invoices/session/${sessionId}`);
    return data.invoices;
  },

  async calculateSplit(totalGross: number, fundingPercent?: number, taxRate?: number): Promise<SplitCalculation> {
    const params: Record<string, any> = { totalGross };
    if (fundingPercent !== undefined) params.fundingPercent = fundingPercent;
    if (taxRate !== undefined) params.taxRate = taxRate;
    const { data } = await api.get('/invoices/calculate', { params });
    return data.split;
  },

  async updateInvoiceStatus(id: number, updates: {
    fundingStatus?: InvoiceStatus; ownStatus?: InvoiceStatus; notes?: string;
  }): Promise<void> {
    await api.patch(`/invoices/${id}`, updates);
  },

  async syncInvoiceToFakturownia(id: number): Promise<{ fundingInvoiceId: number | null; ownInvoiceId: number | null }> {
    const { data } = await api.post(`/invoices/${id}/sync`);
    return data;
  },

  // ─── Moduł 2: Resources & Conflicts ──────────────────────────────

  async getResources(filters?: { companyId?: number; type?: ResourceType; activeOnly?: boolean }): Promise<TrainingResource[]> {
    const { data } = await api.get('/resources', { params: filters });
    return data.resources;
  },

  async createResource(resource: {
    company_id?: number; resource_type: ResourceType; name: string; description?: string;
    capacity?: number; location?: string; hourly_rate?: number; daily_rate?: number;
    contact_person?: string; contact_phone?: string;
  }): Promise<{ resource: TrainingResource }> {
    const { data } = await api.post('/resources', resource);
    return data;
  },

  async updateResource(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/resources/${id}`, updates);
  },

  async deleteResource(id: number): Promise<void> {
    await api.delete(`/resources/${id}`);
  },

  async checkResourceAvailability(id: number, dateFrom: string, dateTo: string): Promise<AvailabilityResult> {
    const { data } = await api.get(`/resources/${id}/availability`, { params: { dateFrom, dateTo } });
    return data;
  },

  async checkTrainerAvailability(id: number, dateFrom: string, dateTo: string): Promise<AvailabilityResult> {
    const { data } = await api.get(`/trainers/${id}/availability`, { params: { dateFrom, dateTo } });
    return data;
  },

  async checkSessionConflicts(sessionId: number, params: {
    trainerId?: number; resourceIds?: number[]; startDate: string; endDate: string;
  }): Promise<{ valid: boolean; conflicts: ConflictInfo[] }> {
    const { data } = await api.post(`/sessions/${sessionId}/check-conflicts`, params);
    return data;
  },

  // ─── Moduł 3: Tests & BUR Compliance ──────────────────────────────

  async getTestsForCourse(courseId: number): Promise<TrainingTest[]> {
    const { data } = await api.get(`/courses/${courseId}/tests`);
    return data.tests;
  },

  async createTest(courseId: number, test: {
    testType: TestType; title: string; description?: string;
    questions?: TestQuestion[]; passingScore?: number; timeLimitMinutes?: number;
  }): Promise<{ test: TrainingTest }> {
    const { data } = await api.post(`/courses/${courseId}/tests`, test);
    return data;
  },

  async updateTest(id: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/tests/${id}`, updates);
  },

  async submitTestResponse(testId: number, enrollmentId: number, answers: Record<string, any>): Promise<{ response: TestResponse }> {
    const { data } = await api.post(`/tests/${testId}/submit`, { enrollmentId, answers });
    return data;
  },

  async getTestResults(testId: number): Promise<TestResponse[]> {
    const { data } = await api.get(`/tests/${testId}/results`);
    return data.results;
  },

  async downloadParpAttendance(sessionId: number): Promise<Blob> {
    const { data } = await api.post(`/sessions/${sessionId}/parp-attendance`, {}, { responseType: 'blob' });
    return data;
  },

  async downloadTestPdf(sessionId: number, testId: number, type: 'pre' | 'post'): Promise<Blob> {
    const endpoint = type === 'pre' ? 'pre-test-pdf' : 'post-test-pdf';
    const { data } = await api.post(`/sessions/${sessionId}/${endpoint}`, { testId }, { responseType: 'blob' });
    return data;
  },

  async checkBurCompliance(sessionId: number): Promise<BurComplianceResult> {
    const { data } = await api.get(`/sessions/${sessionId}/bur-compliance`);
    return data;
  },

  // ─── Entity Email ─────────────────────────────────────────────────

  async getEntityEmailConfig(companyId: number): Promise<EntityEmailConfig> {
    const { data } = await api.get(`/companies/${companyId}/email-config`);
    return data.config;
  },

  async verifyEntitySmtp(companyId: number): Promise<{ configured: boolean; valid: boolean; error?: string }> {
    const { data } = await api.post(`/companies/${companyId}/email-verify`);
    return data;
  },

  async sendTestEmail(companyId: number, to: string): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const { data } = await api.post(`/companies/${companyId}/email-test`, { to });
    return data;
  },
};

// ─── Helpers ────────────────────────────────────────────────────────

export const FUNDING_COLORS: Record<FundingSource, { bg: string; text: string; label: string }> = {
  BUR: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'BUR' },
  KFS: { bg: 'bg-green-100', text: 'text-green-700', label: 'KFS' },
  KOMERCYJNE: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Komercyjne' },
  EFS: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'EFS' },
  INNE: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Inne' },
};

export const SESSION_STATUS_LABELS: Record<SessionStatus, { label: string; color: string }> = {
  planowana: { label: 'Planowana', color: 'bg-gray-100 text-gray-700' },
  rekrutacja: { label: 'Rekrutacja', color: 'bg-blue-100 text-blue-700' },
  potwierdźona: { label: 'Potwierdzona', color: 'bg-indigo-100 text-indigo-700' },
  w_trakcie: { label: 'W trakcie', color: 'bg-green-100 text-green-700' },
  zakończona: { label: 'Zakończona', color: 'bg-emerald-100 text-emerald-700' },
  anulowana: { label: 'Anulowana', color: 'bg-red-100 text-red-700' },
};

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  zapisany: 'Zapisany',
  potwierdźony: 'Potwierdzony',
  obecny: 'Uczestniczył',
  ukończony: 'Ukończony',
  certyfikowany: 'Certyfikowany',
  zrezygnowany: 'Rezygnacja',
  nieobecny: 'Nieobecny',
};

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, { label: string; color: string }> = {
  oczekująca: { label: 'Oczekująca', color: 'bg-amber-100 text-amber-700' },
  potwierdźona: { label: 'Potwierdzona', color: 'bg-blue-100 text-blue-700' },
  opłacona: { label: 'Opłacona', color: 'bg-green-100 text-green-700' },
  zapisana: { label: 'Zapisana', color: 'bg-emerald-100 text-emerald-700' },
  anulowana: { label: 'Anulowana', color: 'bg-red-100 text-red-700' },
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, { label: string; color: string }> = {
  nieopłacone: { label: 'Nieopłacone', color: 'bg-gray-100 text-gray-700' },
  proforma_wysłana: { label: 'Proforma', color: 'bg-blue-100 text-blue-700' },
  opłacone: { label: 'Zapłacono', color: 'bg-green-100 text-green-700' },
  zwrócone: { label: 'Zwrot', color: 'bg-red-100 text-red-700' },
};

export const PAYMENT_PATH_LABELS: Record<PaymentPath, string> = {
  samodzielna: 'Własne środki',
  dofinansowanie_bur: 'Dofinansowanie BUR',
  finansowanie_kfs: 'Finansowanie KFS',
};

export const STATUS_TRANSITIONS: Record<SessionStatus, SessionStatus[]> = {
  planowana: ['rekrutacja', 'anulowana'],
  rekrutacja: ['potwierdźona', 'anulowana'],
  potwierdźona: ['w_trakcie', 'anulowana'],
  w_trakcie: ['zakończona', 'anulowana'],
  zakończona: [],
  anulowana: [],
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, { label: string; color: string }> = {
  oczekująca: { label: 'Oczekuje', color: 'bg-gray-100 text-gray-700' },
  wystawiona: { label: 'Wystawiona', color: 'bg-blue-100 text-blue-700' },
  wysłana: { label: 'Wysłana', color: 'bg-indigo-100 text-indigo-700' },
  opłacona: { label: 'Zapłacona', color: 'bg-green-100 text-green-700' },
  anulowana: { label: 'Anulowana', color: 'bg-red-100 text-red-700' },
};

export const RESOURCE_TYPE_LABELS: Record<ResourceType, { label: string; icon: string }> = {
  sala: { label: 'Sala', icon: '🏢' },
  catering: { label: 'Catering', icon: '🍽️' },
  sprzęt: { label: 'Sprzęt', icon: '💻' },
  inne: { label: 'Inne', icon: '📦' },
};

export const TEST_TYPE_LABELS: Record<TestType, string> = {
  test_wstępny: 'Test wstępny',
  test_końcowy: 'Test końcowy',
};

// ─── KFS 2026 Types ─────────────────────────────────────────────────

export interface KfsPupOffice {
  id: number;
  name: string;
  office_type: string;
  voivodeship: string;
  city: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  praca_gov_url: string | null;
  budget_pln: number | null;
  budget_year: number;
  ministerial_priorities: string[];
  wrrp_priorities: string[];
  local_priority: string | null;
  nabor_dates: string | null;
  nabor_status: string;
  max_percent_micro: number;
  max_percent_other: number;
  notes: string | null;
  is_active: boolean;
}

export interface KfsChecklistItem {
  id: number;
  session_id: number;
  phase: 'before' | 'during' | 'after';
  item_key: string;
  item_label: string;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: number | null;
  completed_by_name: string | null;
  notes: string | null;
  due_date: string | null;
}

export interface KfsStats {
  pupOffices: { total: number; openNabor: number };
  sessions: { total: number; approved: number; settled: number; totalFunding: number };
  budget: { voivodeships: number; totalBudget: number };
  checklist: { total: number; completed: number };
}

export interface KfsPriorities {
  ministerial: { id: number; title: string; description: string }[];
  regional: { voivodeship: string; wrrpPriorities: string[]; pupCount: number; totalBudget: number }[];
  legalBasis: string;
}

export interface KfsLeadsDashboardStats {
  total_leads: number;
  leads_by_status: Record<string, number>;
  leads_by_source: Record<string, number>;
  leads_by_day: { date: string; count: number }[];
  leads_by_city: { city: string; count: number }[];
  conversion_rate: number;
  avg_lead_score: number;
}

// KFS Analytics types
export interface KfsFunnelStage {
  name: string;
  count: number;
  conversion: number;
  avg_days: number;
}
export interface KfsFunnelData {
  stages: KfsFunnelStage[];
  overall_conversion: number;
}
export interface KfsRoiData {
  total_funding: number;
  funded_sessions: number;
  total_revenue: number;
  enrollment_count: number;
  total_leads: number;
  cost_per_lead: number;
  customer_acquisition_cost: number;
  avg_deal_value: number;
  pipeline_value: number;
  pipeline_count: number;
  conversion_rate: number;
}
export interface KfsRegionalItem {
  powiat: string;
  lead_count: number;
  verified_count: number;
  conversion_rate: number;
  avg_score: number;
  converted_count: number;
  active_nabory: number;
  total_budget: number;
  prev_quarter_leads: number;
  growth: number | null;
}
export interface KfsRegionalData {
  regions: KfsRegionalItem[];
}
export interface KfsTrendDay {
  date: string;
  new_leads: number;
  applications: number;
  ma_leads: number;
}
export interface KfsTrendData {
  daily: KfsTrendDay[];
  weekly: { week: string; new_leads: number; verified: number }[];
  yoy: { current: number; previous_year: number; growth_pct: number | null };
  period_days: number;
}
export interface KfsTopLead {
  id: number;
  company_name: string;
  nip: string | null;
  lead_score: number;
  kfs_zakres: string | null;
  status: string;
  powiat: string;
  created_at: string;
  company_size: string | null;
  employee_count: number | null;
  email: string | null;
}

export interface KfsPipelineCard {
  id: string;
  source: 'kfs_lead' | 'crm_application';
  source_id: number;
  company_name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  lead_score: number | null;
  kfs_zakres: string | null;
  stage: string;
  days_in_stage: number;
  created_at: string;
  assigned_to: string | null;
  nip: string | null;
}

export interface KfsPipelineColumn {
  count: number;
  cards: KfsPipelineCard[];
}

export interface KfsPipelineData {
  columns: Record<string, KfsPipelineColumn>;
  total: number;
  stages: string[];
}

// KFS SMS Drip Campaign types
export interface KfsSmsTemplate {
  id: number;
  name: string;
  body: string;
  variables: string[];
  char_count: number;
  is_system: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface KfsSmsCampaignStep {
  delay_hours: number;
  template_id?: number;
  template_name?: string;
  condition?: string;
}

export interface KfsSmsCampaign {
  id: number;
  name: string;
  trigger_type: 'new_lead' | 'nabor_open' | 'no_response_3d' | 'no_response_7d' | 'post_training';
  status: 'draft' | 'active' | 'completed' | 'paused';
  steps: KfsSmsCampaignStep[];
  metadata: Record<string, any>;
  total_logs: number;
  last_executed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface KfsSmsPreviewMessage {
  lead_id: number;
  lead_name: string;
  phone: string;
  company: string;
  powiat: string;
  step_index: number;
  delay_hours: number;
  template_name: string;
  rendered_message: string;
  char_count: number;
  multi_sms: boolean;
}

export interface KfsSmsPreviewResult {
  campaign_id: number;
  campaign_name: string;
  trigger_type: string;
  matching_leads: number;
  total_messages: number;
  steps_count: number;
  messages: KfsSmsPreviewMessage[];
  dry_run: true;
}

export interface KfsSmsExecuteResult {
  dry_run: true;
  campaign_id: number;
  campaign_name: string;
  would_send: number;
  leads_matched: number;
  messages: any[];
  message: string;
}

export const TRIGGER_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  new_lead: { label: 'Nowy lead', color: 'bg-blue-100 text-blue-700' },
  nabor_open: { label: 'Nabor otwarty', color: 'bg-green-100 text-green-700' },
  no_response_3d: { label: 'Brak odpowiedzi 3d', color: 'bg-amber-100 text-amber-700' },
  no_response_7d: { label: 'Brak odpowiedzi 7d', color: 'bg-orange-100 text-orange-700' },
  post_training: { label: 'Po szkoleniu', color: 'bg-purple-100 text-purple-700' },
};

export const SMS_CAMPAIGN_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Szkic', color: 'bg-gray-100 text-gray-700' },
  active: { label: 'Aktywna', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Zakonczona', color: 'bg-blue-100 text-blue-700' },
  paused: { label: 'Wstrzymana', color: 'bg-amber-100 text-amber-700' },
};

export const SMS_VARIABLES = [
  { key: '{{firma}}', label: 'Firma' },
  { key: '{{imie}}', label: 'Imie' },
  { key: '{{zakres}}', label: 'Zakres szkolenia' },
  { key: '{{powiat}}', label: 'Powiat' },
  { key: '{{kwota_dotacji}}', label: 'Kwota dotacji' },
  { key: '{{termin_naboru}}', label: 'Termin naboru' },
];

// ── KFS Outreach Template types ──

export type KfsFunnelStageKey = 'cold_intro' | 'follow_up' | 'interested' | 'proposal' | 'enrollment' | 'post_training' | 'referral' | 'reactivation';

export interface KfsOutreachTemplate {
  id: number;
  name: string;
  stage: KfsFunnelStageKey;
  channel: 'email' | 'sms';
  subject: string | null;
  body: string;
  variables: string[];
  is_default: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface KfsOutreachBulkPreviewItem {
  lead_id: number;
  company_name: string;
  subject?: string;
  body_preview: string;
  ready: boolean;
  missing: string[];
}

export interface KfsOutreachTemplateStats {
  total: number;
  by_stage: Record<string, number>;
  by_channel: Record<string, number>;
  default_count: number;
  custom_count: number;
  stage_labels: Record<string, string>;
}

export const OUTREACH_FUNNEL_STAGES: { key: KfsFunnelStageKey; label: string; color: string; icon: string }[] = [
  { key: 'cold_intro', label: 'Pierwszy kontakt', color: 'bg-blue-100 text-blue-700', icon: '📨' },
  { key: 'follow_up', label: 'Follow-up', color: 'bg-cyan-100 text-cyan-700', icon: '🔄' },
  { key: 'interested', label: 'Zainteresowany', color: 'bg-green-100 text-green-700', icon: '✅' },
  { key: 'proposal', label: 'Propozycja', color: 'bg-emerald-100 text-emerald-700', icon: '📋' },
  { key: 'enrollment', label: 'Zapis / wniosek', color: 'bg-indigo-100 text-indigo-700', icon: '📝' },
  { key: 'post_training', label: 'Po szkoleniu', color: 'bg-purple-100 text-purple-700', icon: '🎓' },
  { key: 'referral', label: 'Polecenie', color: 'bg-pink-100 text-pink-700', icon: '🤝' },
  { key: 'reactivation', label: 'Reaktywacja', color: 'bg-amber-100 text-amber-700', icon: '♻️' },
];

export const OUTREACH_VARIABLES: { key: string; label: string; category: string }[] = [
  // Company
  { key: '{{firma}}', label: 'Nazwa firmy', category: 'Firma' },
  { key: '{{nip}}', label: 'NIP', category: 'Firma' },
  { key: '{{branza}}', label: 'Branża (PKD)', category: 'Firma' },
  { key: '{{pkd}}', label: 'Kod PKD', category: 'Firma' },
  { key: '{{wielkosc_firmy}}', label: 'Wielkość firmy', category: 'Firma' },
  { key: '{{miasto}}', label: 'Miasto', category: 'Firma' },
  // Contact
  { key: '{{imie}}', label: 'Imię kontaktu', category: 'Kontakt' },
  { key: '{{nazwisko}}', label: 'Nazwisko kontaktu', category: 'Kontakt' },
  // KFS
  { key: '{{procent_dofinansowania}}', label: '% dofinansowania', category: 'KFS' },
  { key: '{{procent}}', label: '% (krótko)', category: 'KFS' },
  { key: '{{kwota_dofinansowania}}', label: 'Kwota dofinansowania', category: 'KFS' },
  { key: '{{pup}}', label: 'PUP (powiat)', category: 'KFS' },
  { key: '{{pup_nazwa}}', label: 'Pełna nazwa PUP', category: 'KFS' },
  { key: '{{termin_naboru}}', label: 'Termin naboru', category: 'KFS' },
  { key: '{{status_naboru}}', label: 'Status naboru', category: 'KFS' },
  { key: '{{pula_srodkow}}', label: 'Pula środków', category: 'KFS' },
  { key: '{{priorytety}}', label: 'Priorytety MRiPS', category: 'KFS' },
  // Training
  { key: '{{zakres_szkolen}}', label: 'Zakres szkoleń', category: 'Szkolenie' },
  { key: '{{zakres}}', label: 'Zakres (krótko)', category: 'Szkolenie' },
  { key: '{{nazwa_szkolenia}}', label: 'Nazwa szkolenia', category: 'Szkolenie' },
  { key: '{{termin_szkolenia}}', label: 'Termin szkolenia', category: 'Szkolenie' },
  { key: '{{czas_trwania}}', label: 'Czas trwania', category: 'Szkolenie' },
  { key: '{{liczba_uczestnikow}}', label: 'Liczba uczestników', category: 'Szkolenie' },
  { key: '{{koszt_brutto}}', label: 'Koszt brutto', category: 'Szkolenie' },
  { key: '{{wklad_wlasny}}', label: 'Wkład własny', category: 'Szkolenie' },
  { key: '{{zakres_szczegolowy}}', label: 'Zakres szczegółowy', category: 'Szkolenie' },
  { key: '{{trener}}', label: 'Trener', category: 'Szkolenie' },
  { key: '{{trener_kwalifikacje}}', label: 'Kwalifikacje trenera', category: 'Szkolenie' },
  // Handlowiec
  { key: '{{handlowiec_imie}}', label: 'Imię handlowca', category: 'Handlowiec' },
  { key: '{{handlowiec_telefon}}', label: 'Telefon handlowca', category: 'Handlowiec' },
  { key: '{{handlowiec_email}}', label: 'E-mail handlowca', category: 'Handlowiec' },
  { key: '{{handlowiec_stanowisko}}', label: 'Stanowisko handlowca', category: 'Handlowiec' },
  { key: '{{handlowiec}}', label: 'Handlowiec (krótko)', category: 'Handlowiec' },
  { key: '{{tel}}', label: 'Telefon (krótko)', category: 'Handlowiec' },
  // Other
  { key: '{{skutecznosc}}', label: 'Skuteczność FPN', category: 'Inne' },
  { key: '{{rok}}', label: 'Rok', category: 'Inne' },
  { key: '{{link}}', label: 'Link', category: 'Inne' },
  { key: '{{link_polecenia}}', label: 'Link polecenia', category: 'Inne' },
  { key: '{{kod_polecenia}}', label: 'Kod polecenia', category: 'Inne' },
  { key: '{{bonus_polecenie}}', label: 'Bonus polecenie', category: 'Inne' },
  { key: '{{link_ankiety}}', label: 'Link ankiety', category: 'Inne' },
];

export const kfsApi = {
  // PUP Offices
  async getPupOffices(filters?: { voivodeship?: string; search?: string; nabor_status?: string }): Promise<KfsPupOffice[]> {
    const params = new URLSearchParams();
    if (filters?.voivodeship) params.set('voivodeship', filters.voivodeship);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.nabor_status) params.set('nabor_status', filters.nabor_status);
    const { data } = await api.get(`/kfs/pup-offices?${params}`);
    return data.offices;
  },

  async getPupOffice(id: number): Promise<{ office: KfsPupOffice; sessions: any[] }> {
    const { data } = await api.get(`/kfs/pup-offices/${id}`);
    return data;
  },

  async createPupOffice(office: Partial<KfsPupOffice>): Promise<{ id: number }> {
    const { data } = await api.post('/kfs/pup-offices', office);
    return data;
  },

  async updatePupOffice(id: number, updates: Partial<KfsPupOffice>): Promise<void> {
    await api.patch(`/kfs/pup-offices/${id}`, updates);
  },

  // Stats & Priorities
  async getStats(): Promise<KfsStats> {
    const { data } = await api.get('/kfs/stats');
    return data;
  },

  async getPriorities(): Promise<KfsPriorities> {
    const { data } = await api.get('/kfs/priorities');
    return data;
  },

  // Provider Checklist
  async getChecklist(sessionId: number): Promise<KfsChecklistItem[]> {
    const { data } = await api.get(`/kfs/checklist/${sessionId}`);
    return data.checklist;
  },

  async updateChecklistItem(id: number, updates: { is_completed?: boolean; notes?: string }): Promise<void> {
    await api.patch(`/kfs/checklist/${id}`, updates);
  },

  // Session KFS fields
  async updateSessionKfs(sessionId: number, updates: Record<string, any>): Promise<void> {
    await api.patch(`/sessions/${sessionId}/kfs`, updates);
  },

  // Provider obligations reference
  async getProviderObligations(): Promise<any> {
    const { data } = await api.get('/kfs/provider-obligations');
    return data;
  },

  // Nabory monitoring
  async getNabory(): Promise<any[]> {
    const { data } = await api.get('/kfs/nabory');
    return data;
  },

  async getNaborStats(): Promise<{ total: number; open: number; closed: number; unknown: number; lastScrape: string | null }> {
    const { data } = await api.get('/kfs/nabory/stats');
    return data;
  },

  async triggerNaborScrape(): Promise<{ processed: number; changed: number; errors: number }> {
    const { data } = await api.post('/kfs/nabory/scrape');
    return data;
  },

  // Document analysis (Faza 2a)
  async analyzeKfsDocument(applicationId: number, ocrText: string): Promise<any> {
    const { data } = await api.post('/kfs/analyze-document', { applicationId, ocrText });
    return data;
  },

  async getChecklistStatus(applicationId: number): Promise<{
    complete: boolean; totalRequired: number; uploadedCount: number; percentage: number;
    documents: Array<{ key: string; label: string; required: boolean; uploaded: boolean; ocrConfidence?: number }>;
  }> {
    const { data } = await api.get(`/kfs/checklist-status/${applicationId}`);
    return data;
  },

  // Generator KFS++ (Faza 2b)
  async autoScoreApplication(applicationId: number): Promise<any> {
    const { data } = await api.post(`/kfs/auto-score/${applicationId}`);
    return data;
  },

  async generatePackage(applicationId: number, strategy: string = 'A'): Promise<any> {
    const { data } = await api.post(`/kfs/generate-package/${applicationId}`, { strategy });
    return data;
  },

  async batchScore(limit: number = 50): Promise<{ message: string; count: number }> {
    const { data } = await api.post('/kfs/batch-score', { limit });
    return data;
  },

  async getScores(): Promise<any[]> {
    const { data } = await api.get('/kfs/scores');
    return data;
  },

  async generateProposals(limit: number = 100): Promise<any> {
    const { data } = await api.post('/kfs/generate-proposals', { limit });
    return data;
  },

  async getProposals(): Promise<any[]> {
    const { data } = await api.get('/kfs/proposals');
    return data;
  },

  async sendProposalEmail(id: number): Promise<any> {
    const { data } = await api.post(`/kfs/proposals/${id}/send-email`);
    return data;
  },

  async approveProposal(id: number): Promise<any> {
    const { data } = await api.post(`/kfs/proposals/${id}/approve`);
    return data;
  },

  async batchSendProposals(): Promise<any> {
    const { data } = await api.post('/kfs/proposals/batch-send');
    return data;
  },

  // Outreach AI
  async getOutreachCandidates(params?: { minScore?: number; limit?: number; status?: string }): Promise<any> {
    const qs = new URLSearchParams();
    if (params?.minScore) qs.set('minScore', String(params.minScore));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const { data } = await api.get(`/kfs/outreach/candidates?${qs}`);
    return data;
  },

  async getOutreachStats(): Promise<any> {
    const { data } = await api.get('/kfs/outreach/stats');
    return data;
  },

  async previewOutreach(params: { candidateIds?: number[]; useAI?: boolean; limit?: number }): Promise<any> {
    const { data } = await api.post('/kfs/outreach/preview', params);
    return data;
  },

  async previewOutreachHtml(id: number, useAI: boolean = true): Promise<any> {
    const { data } = await api.post(`/kfs/outreach/preview/${id}/html`, { useAI });
    return data;
  },

  async sendOutreach(params: { candidateIds?: number[]; useAI?: boolean; dryRun?: boolean; limit?: number }): Promise<any> {
    const { data } = await api.post('/kfs/outreach/send', params);
    return data;
  },

  // KFS Overlap Detection
  async checkKfsOverlap(params: { pesel?: string; email?: string; firstName?: string; lastName?: string; kfsZakres: string; excludeSessionId?: number }): Promise<any> {
    const { data } = await api.post('/kfs/overlap-check', params);
    return data;
  },

  async getParticipantKfsHistory(participantId: number): Promise<any> {
    const { data } = await api.get(`/kfs/participant-history/${participantId}`);
    return data;
  },

  async getCompanyKfsHistory(nip: string): Promise<any> {
    const { data } = await api.get(`/kfs/company-history/${nip}`);
    return data;
  },

  async getKfsComplianceReport(): Promise<any> {
    const { data } = await api.get('/kfs/compliance-report');
    return data;
  },

  async getKfsZakresLabels(): Promise<Record<string, string>> {
    const { data } = await api.get('/kfs/zakres-labels');
    return data;
  },

  // KFS Leads Dashboard Stats
  async getLeadsDashboardStats(): Promise<KfsLeadsDashboardStats> {
    const { data } = await api.get('/kfs/dashboard-stats');
    return data;
  },

  // KFS Pipeline Kanban
  async getPipeline(): Promise<KfsPipelineData> {
    const { data } = await api.get('/kfs/pipeline');
    return data;
  },

  // KFS SMS Drip Campaigns
  async getSmsCampaigns(): Promise<{ campaigns: KfsSmsCampaign[] }> {
    const { data } = await api.get('/kfs/sms/campaigns');
    return data;
  },
  async createSmsCampaign(payload: { name: string; trigger_type: string; steps: KfsSmsCampaignStep[] }): Promise<{ id: number }> {
    const { data } = await api.post('/kfs/sms/campaigns', payload);
    return data;
  },
  async updateSmsCampaign(id: number, payload: Partial<KfsSmsCampaign>): Promise<void> {
    await api.put(`/kfs/sms/campaigns/${id}`, payload);
  },
  async deleteSmsCampaign(id: number): Promise<void> {
    await api.delete(`/kfs/sms/campaigns/${id}`);
  },
  async getSmsTemplates(): Promise<{ templates: KfsSmsTemplate[] }> {
    const { data } = await api.get('/kfs/sms/templates');
    return data;
  },
  async createSmsTemplate(payload: { name: string; body: string; variables?: string[] }): Promise<{ id: number; char_count: number; warning: string | null }> {
    const { data } = await api.post('/kfs/sms/templates', payload);
    return data;
  },
  async updateSmsTemplate(id: number, payload: { name?: string; body?: string; variables?: string[] }): Promise<void> {
    await api.put(`/kfs/sms/templates/${id}`, payload);
  },
  async deleteSmsTemplate(id: number): Promise<void> {
    await api.delete(`/kfs/sms/templates/${id}`);
  },
  async previewCampaign(id: number): Promise<KfsSmsPreviewResult> {
    const { data } = await api.post(`/kfs/sms/campaigns/${id}/preview`, {});
    return data;
  },
  async executeCampaign(id: number): Promise<KfsSmsExecuteResult> {
    const { data } = await api.post(`/kfs/sms/campaigns/${id}/execute`, {});
    return data;
  },
  async getCampaignLogs(id: number): Promise<{ logs: any[] }> {
    const { data } = await api.get(`/kfs/sms/campaigns/${id}/logs`);
    return data;
  },

  // KFS Advanced Analytics
  async getFunnelAnalytics(params?: { start?: string; end?: string; powiat?: string }): Promise<KfsFunnelData> {
    const qs = new URLSearchParams();
    if (params?.start) qs.set('start', params.start);
    if (params?.end) qs.set('end', params.end);
    if (params?.powiat) qs.set('powiat', params.powiat);
    const { data } = await api.get(`/kfs/analytics/funnel?${qs}`);
    return data;
  },
  async getRoiAnalytics(): Promise<KfsRoiData> {
    const { data } = await api.get('/kfs/analytics/roi');
    return data;
  },
  async getRegionalAnalytics(): Promise<KfsRegionalData> {
    const { data } = await api.get('/kfs/analytics/regional');
    return data;
  },
  async getTrendAnalytics(days?: number): Promise<KfsTrendData> {
    const { data } = await api.get(`/kfs/analytics/trends?days=${days || 30}`);
    return data;
  },
  async getTopLeads(limit?: number): Promise<{ leads: KfsTopLead[] }> {
    const { data } = await api.get(`/kfs/analytics/top-leads?limit=${limit || 20}`);
    return data;
  },

  // KFS Document Generator (Wniosek KFS)
  async generateWniosek(leadId: number, sessionId?: number, template?: 'standard' | 'priority' | 'micro'): Promise<{ document: KfsWniosekDocument; lead_id: number }> {
    const { data } = await api.post('/kfs/documents/generate-wniosek', { lead_id: leadId, session_id: sessionId, template });
    return data;
  },
  async getDocuments(leadId: number): Promise<{ lead_id: number; documents: any[]; checklist: any }> {
    const { data } = await api.get(`/kfs/documents/${leadId}`);
    return data;
  },
  async generateUzasadnienie(leadId: number): Promise<{ uzasadnienie: string; lead_id: number }> {
    const { data } = await api.post('/kfs/documents/generate-uzasadnienie', { lead_id: leadId });
    return data;
  },

  // KFS Calendar V2
  async getCalendarEvents(start: string, end: string, types?: string): Promise<{ events: KfsCalendarEvent[] }> {
    const params = new URLSearchParams({ start, end });
    if (types) params.set('types', types);
    const { data } = await api.get(`/kfs/calendar?${params}`);
    return data;
  },
  async addCalendarNabor(payload: { pup_name: string; start_date: string; end_date: string; notes?: string; budget_amount?: number }): Promise<any> {
    const { data } = await api.post('/kfs/calendar/nabory', payload);
    return data;
  },
  async getCalendarNabory(): Promise<{ nabory: KfsCalendarNabor[] }> {
    const { data } = await api.get('/kfs/calendar/nabory-list');
    return data;
  },

  // ── CEIDG Scraper ──────────────────────────────────────────────────

  async scrapeCeidg(cities?: string[]): Promise<any> {
    const { data } = await api.post('/kfs/ceidg/scrape', { cities });
    return data;
  },
  async getCeidgStats(): Promise<any> {
    const { data } = await api.get('/kfs/ceidg/stats');
    return data;
  },
  async enrichFromCeidg(nip: string): Promise<any> {
    const { data } = await api.post(`/kfs/ceidg/enrich/${nip}`);
    return data;
  },

  // ── GUS/CEIDG Enrichment ───────────────────────────────────────────

  async bulkEnrich(options?: { limit?: number; onlyMissingPkd?: boolean; delayMs?: number }): Promise<{
    total_processed: number; enriched: number; failed: number; already_enriched: number;
    errors: Array<{ nip: string; error: string }>;
  }> {
    const { data } = await api.post('/kfs/enrich/bulk', options);
    return data;
  },
  async enrichByNip(nip: string): Promise<{
    nip: string; company_name?: string; pkd_main?: string; pkd_description?: string;
    pkd_all?: string[]; legal_form?: string; employee_count_range?: string;
    regon?: string; address_full?: string; source: string; enriched_at: string;
  }> {
    const { data } = await api.post(`/kfs/enrich/${nip}`);
    return data;
  },
  async getEnrichmentStats(): Promise<{
    total_leads: number; enriched: number; not_enriched: number;
    by_pkd: Array<{ pkd: string; description: string; count: number }>;
    by_employee_range: Array<{ range: string; count: number }>;
    by_legal_form: Array<{ form: string; count: number }>;
  }> {
    const { data } = await api.get('/kfs/enrich/stats');
    return data;
  },
  async getPkdDescriptions(): Promise<Record<string, string>> {
    const { data } = await api.get('/kfs/enrich/pkd-descriptions');
    return data;
  },

  // ── Deduplication ──────────────────────────────────────────────────

  async scanDuplicates(): Promise<any> {
    const { data } = await api.get('/kfs/dedup/scan');
    return data;
  },
  async mergeLeads(primaryId: number, secondaryIds: number[], strategy?: string): Promise<any> {
    const { data } = await api.post('/kfs/dedup/merge', { primaryId, secondaryIds, strategy });
    return data;
  },
  async autoMerge(dryRun?: boolean): Promise<any> {
    const { data } = await api.post('/kfs/dedup/auto-merge', { dryRun });
    return data;
  },
  async getDedupStats(): Promise<any> {
    const { data } = await api.get('/kfs/dedup/stats');
    return data;
  },

  // ── Auto-Matching ──────────────────────────────────────────────────

  async matchLead(leadId: number): Promise<any> {
    const { data } = await api.post(`/kfs/matching/lead/${leadId}`);
    return data;
  },
  async bulkMatch(options?: { limit?: number; minScore?: number }): Promise<any> {
    const { data } = await api.post('/kfs/matching/bulk', options);
    return data;
  },
  async findLeadsForSession(sessionId: number): Promise<any> {
    const { data } = await api.get(`/kfs/matching/session/${sessionId}`);
    return data;
  },
  async autoAssign(matches: Array<{ leadId: number; sessionId: number; score?: number }>, dryRun?: boolean): Promise<any> {
    const { data } = await api.post('/kfs/matching/auto-assign', { matches, dryRun });
    return data;
  },
  async getMatchingStats(): Promise<any> {
    const { data } = await api.get('/kfs/matching/stats');
    return data;
  },

  // === KFS Handlowiec Dashboard ===
  async getHandlowiecDashboard(): Promise<KfsHandlowiecDashboardData> {
    const { data } = await api.get('/kfs/handlowiec/dashboard');
    return data;
  },
  async getMyLeads(params?: { status?: string; city?: string; limit?: number }): Promise<KfsHandlowiecLead[]> {
    const { data } = await api.get('/kfs/handlowiec/my-leads', { params });
    return data;
  },
  async quickAction(action: string, leadId: number, data?: any): Promise<{ success: boolean; action: string; leadId: number }> {
    const res = await api.post('/kfs/handlowiec/quick-action', { action, leadId, ...data });
    return res.data;
  },

  // === KFS Outreach Templates ===

  async getOutreachTemplates(stage?: string, channel?: string): Promise<{ templates: KfsOutreachTemplate[]; total: number }> {
    const params = new URLSearchParams();
    if (stage) params.set('stage', stage);
    if (channel) params.set('channel', channel);
    const { data } = await api.get(`/kfs/outreach/templates?${params}`);
    return data;
  },
  async getOutreachTemplate(id: number): Promise<{ template: KfsOutreachTemplate }> {
    const { data } = await api.get(`/kfs/outreach/templates/${id}`);
    return data;
  },
  async getOutreachTemplateStats(): Promise<KfsOutreachTemplateStats> {
    const { data } = await api.get('/kfs/outreach/templates/stats');
    return data;
  },
  async createOutreachTemplate(payload: {
    name: string;
    stage: string;
    channel: 'email' | 'sms';
    subject?: string;
    body: string;
    variables?: string[];
  }): Promise<{ template: KfsOutreachTemplate; message: string }> {
    const { data } = await api.post('/kfs/outreach/templates', payload);
    return data;
  },
  async updateOutreachTemplate(id: number, payload: Partial<{
    name: string;
    stage: string;
    channel: 'email' | 'sms';
    subject: string | null;
    body: string;
    variables: string[];
  }>): Promise<{ template: KfsOutreachTemplate; message: string }> {
    const { data } = await api.put(`/kfs/outreach/templates/${id}`, payload);
    return data;
  },
  async deleteOutreachTemplate(id: number): Promise<{ message: string }> {
    const { data } = await api.delete(`/kfs/outreach/templates/${id}`);
    return data;
  },
  async previewOutreachTemplate(templateId: number, leadId: number, extraVars?: Record<string, string>): Promise<{
    preview: {
      subject?: string;
      body: string;
      variables_used: Record<string, string>;
      missing_variables: string[];
    };
  }> {
    const { data } = await api.post('/kfs/outreach/template-preview', { templateId, leadId, extraVars });
    return data;
  },
  async bulkPreviewOutreachTemplate(templateId: number, leadIds: number[]): Promise<{
    previews: KfsOutreachBulkPreviewItem[];
    total: number;
    ready: number;
    incomplete: number;
  }> {
    const { data } = await api.post('/kfs/outreach/bulk-template-preview', { templateId, leadIds });
    return data;
  },
  async seedOutreachTemplates(): Promise<{ seeded: number; existing: number; message: string }> {
    const { data } = await api.post('/kfs/outreach/seed-templates');
    return data;
  },

  // === KFS Firm Intelligence ===

  async buildFirmProfile(leadId: number): Promise<KfsFirmProfile> {
    const { data } = await api.post(`/kfs/intelligence/profile/${leadId}`);
    return data;
  },
  async bulkBuildProfiles(options?: { limit?: number; onlyMissingWebsite?: boolean; delayMs?: number }): Promise<KfsBulkIntelligenceResult> {
    const { data } = await api.post('/kfs/intelligence/bulk', options);
    return data;
  },
  async getFirmIntelligenceStats(): Promise<KfsFirmIntelligenceStats> {
    const { data } = await api.get('/kfs/intelligence/stats');
    return data;
  },
  async analyzeWebsite(url: string): Promise<KfsWebsiteAnalysis> {
    const { data } = await api.post('/kfs/intelligence/analyze-website', { url });
    return data;
  },
  async getTrainingDemand(): Promise<KfsTrainingDemandResult> {
    const { data } = await api.get('/kfs/intelligence/training-demand');
    return data;
  },
  async findCompanyWebsite(companyName: string, city?: string, nip?: string): Promise<{ website_url: string | null; found: boolean }> {
    const { data } = await api.post('/kfs/intelligence/find-website', { companyName, city, nip });
    return data;
  },
  async calculateDigitalMaturity(profile: Partial<KfsFirmProfile>): Promise<KfsDigitalMaturityResult> {
    const { data } = await api.post('/kfs/intelligence/calculate-maturity', profile);
    return data;
  },

  // === KFS PUP Automation ===

  async getPupAutomationOffices(): Promise<{ offices: KfsPupOfficeInfo[] }> {
    const { data } = await api.get('/kfs/pup/offices');
    return data;
  },
  async getPupNabory(params?: { pup_id?: string; year?: number; status?: string }): Promise<{ nabory: KfsPupNabor[] }> {
    const qs = new URLSearchParams();
    if (params?.pup_id) qs.set('pup_id', params.pup_id);
    if (params?.year) qs.set('year', String(params.year));
    if (params?.status) qs.set('status', params.status);
    const { data } = await api.get(`/kfs/pup/nabory?${qs}`);
    return data;
  },
  async createPupNabor(payload: Partial<KfsPupNabor>): Promise<KfsPupNabor> {
    const { data } = await api.post('/kfs/pup/nabory', payload);
    return data;
  },
  async updatePupNabor(id: number, payload: Partial<KfsPupNabor>): Promise<KfsPupNabor> {
    const { data } = await api.put(`/kfs/pup/nabory/${id}`, payload);
    return data;
  },
  async deletePupNabor(id: number): Promise<void> {
    await api.delete(`/kfs/pup/nabory/${id}`);
  },
  async assignLeadsToPupNabor(naborId: number, leadIds: number[]): Promise<{ assigned: number; already_assigned: number }> {
    const { data } = await api.post(`/kfs/pup/nabory/${naborId}/assign-leads`, { leadIds });
    return data;
  },
  async updatePupNaborLeadStatus(naborId: number, leadId: number, status: string, extra?: { application_date?: string; result_date?: string; notes?: string }): Promise<void> {
    await api.put(`/kfs/pup/nabory/${naborId}/leads/${leadId}`, { status, ...extra });
  },
  async removeLeadFromPupNabor(naborId: number, leadId: number): Promise<void> {
    await api.delete(`/kfs/pup/nabory/${naborId}/leads/${leadId}`);
  },
  async getPupNaborLeads(naborId: number): Promise<{ leads: KfsPupNaborLead[] }> {
    const { data } = await api.get(`/kfs/pup/nabory/${naborId}/leads`);
    return data;
  },
  async autoMatchLeadsToPupNabory(): Promise<{ matched: number; by_pup: Array<{ pup_id: string; pup_name: string; leads_matched: number }> }> {
    const { data } = await api.post('/kfs/pup/nabory/auto-match');
    return data;
  },
  async getPupNaborTimeline(year?: number): Promise<KfsPupNaborTimeline> {
    const qs = year ? `?year=${year}` : '';
    const { data } = await api.get(`/kfs/pup/timeline${qs}`);
    return data;
  },
  async getPupAlerts(): Promise<{ alerts: KfsPupDeadlineAlert[] }> {
    const { data } = await api.get('/kfs/pup/alerts');
    return data;
  },
  async getPupAutomationStats(): Promise<KfsPupAutomationStats> {
    const { data } = await api.get('/kfs/pup/stats');
    return data;
  },
  async seedPupNabory2026(): Promise<{ seeded: number }> {
    const { data } = await api.post('/kfs/pup/seed-2026');
    return data;
  },

  // === KFS PowerPanel ===

  async getPowerPanelDashboard(): Promise<any> {
    const { data } = await api.get('/kfs/powerpanel/dashboard');
    return data;
  },
  async getPowerPanelLeads(params?: any): Promise<any> {
    const { data } = await api.get('/kfs/powerpanel/leads', { params });
    return data;
  },
  async bulkAction(action: string, leadIds: number[], actionData?: any): Promise<any> {
    const { data } = await api.post('/kfs/powerpanel/bulk-action', { action, leadIds, data: actionData });
    return data;
  },

  // === KFS Reporting Engine ===

  async getReportTypes(): Promise<{ types: KfsReportTypeInfo[] }> {
    const { data } = await api.get('/kfs/reports/types');
    return data;
  },

  async generateReport(type: string, params?: { dateFrom?: string; dateTo?: string; pupId?: string; city?: string; handlowiecId?: number }): Promise<any> {
    const { data } = await api.get(`/kfs/reports/${type}`, { params });
    return data;
  },

  async exportReportCsv(type: string, params?: { dateFrom?: string; dateTo?: string; pupId?: string; city?: string; handlowiecId?: number }): Promise<void> {
    const res = await api.get(`/kfs/reports/${type}`, {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kfs-${type}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  async exportLeadsCsv(params?: { status?: string; city?: string; assigned_to?: string; minScore?: number }): Promise<void> {
    const res = await api.get('/kfs/reports/export/leads', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kfs-leads-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // === KFS Email Queue (compose & review) ===

  async composeEmail(templateId: number, leadId: number, customSubject?: string, customBody?: string): Promise<{ email: KfsQueuedEmail; message: string }> {
    const { data } = await api.post('/kfs/email-queue/compose', { templateId, leadId, customSubject, customBody });
    return data;
  },
  async composeBulkEmail(templateId: number, leadIds: number[]): Promise<{
    created: number; errors: number;
    details: { created: { id: number; leadId: number; company: string }[]; errors: { leadId: number; error: string }[] };
    message: string;
  }> {
    const { data } = await api.post('/kfs/email-queue/compose-bulk', { templateId, leadIds });
    return data;
  },
  async getEmailQueue(params?: { status?: string; search?: string; limit?: number; offset?: number; sort?: string }): Promise<{ emails: KfsQueuedEmail[]; total: number; limit: number; offset: number }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.offset) queryParams.set('offset', String(params.offset));
    if (params?.sort) queryParams.set('sort', params.sort);
    const { data } = await api.get(`/kfs/email-queue?${queryParams}`);
    return data;
  },
  async getQueuedEmail(id: number): Promise<{ email: KfsQueuedEmail }> {
    const { data } = await api.get(`/kfs/email-queue/${id}`);
    return data;
  },
  async updateQueuedEmail(id: number, updates: Partial<{ subject: string; body: string; to_email: string; to_name: string; status: string }>): Promise<{ email: KfsQueuedEmail; message: string }> {
    const { data } = await api.put(`/kfs/email-queue/${id}`, updates);
    return data;
  },
  async deleteQueuedEmail(id: number): Promise<{ message: string }> {
    const { data } = await api.delete(`/kfs/email-queue/${id}`);
    return data;
  },
  async approveEmail(id: number): Promise<{ email: KfsQueuedEmail; message: string }> {
    const { data } = await api.post(`/kfs/email-queue/${id}/approve`);
    return data;
  },
  async approveBulkEmails(ids: number[]): Promise<{ approved: number; total_requested: number; skipped: number; message: string }> {
    const { data } = await api.post('/kfs/email-queue/approve-bulk', { ids });
    return data;
  },
  async getEmailQueueStats(): Promise<KfsEmailQueueStats> {
    const { data } = await api.get('/kfs/email-queue/stats');
    return data;
  },

  // === KFS Kanban Board ===

  async getKanbanBoard(filters?: {
    city?: string; pkd?: string; assignedTo?: string; minScore?: number; search?: string;
  }): Promise<KfsKanbanData> {
    const params = new URLSearchParams();
    if (filters?.city) params.set('city', filters.city);
    if (filters?.pkd) params.set('pkd', filters.pkd);
    if (filters?.assignedTo) params.set('assignedTo', filters.assignedTo);
    if (filters?.minScore) params.set('minScore', String(filters.minScore));
    if (filters?.search) params.set('search', filters.search);
    const { data } = await api.get(`/kfs/kanban?${params}`);
    return data;
  },

  async moveKanbanLead(id: number, newStatus: string, note?: string): Promise<{ success: boolean; from: string; to: string }> {
    const { data } = await api.patch(`/kfs/kanban/${id}/move`, { newStatus, note });
    return data;
  },

  async addKanbanNote(id: number, note: string): Promise<{ success: boolean }> {
    const { data } = await api.post(`/kfs/kanban/${id}/note`, { note });
    return data;
  },

  async getKanbanActivity(id: number): Promise<KfsKanbanActivity[]> {
    const { data } = await api.get(`/kfs/kanban/${id}/activity`);
    return data;
  },

  async getKanbanLeadDetail(id: number): Promise<KfsKanbanLeadDetail> {
    const { data } = await api.get(`/kfs/kanban/${id}/detail`);
    return data;
  },

  async assignKanbanLead(id: number, assignedTo: string | null): Promise<{ success: boolean }> {
    const { data } = await api.patch(`/kfs/kanban/${id}/assign`, { assignedTo });
    return data;
  },

  async scheduleKanbanFollowUp(id: number, followUpDate: string | null): Promise<{ success: boolean }> {
    const { data } = await api.patch(`/kfs/kanban/${id}/schedule`, { followUpDate });
    return data;
  },

  // === KFS Lead CSV Import ===

  async importPreview(csvData: string, columnMapping?: Record<string, string>, hasHeader?: boolean): Promise<KfsImportPreviewResult> {
    const { data } = await api.post('/kfs/import/preview', { csvData, columnMapping, hasHeader });
    return data;
  },

  async importExecute(rows: any[], options?: { source?: string; skipDuplicates?: boolean; updateExisting?: boolean }): Promise<KfsImportExecuteResult> {
    const { data } = await api.post('/kfs/import/execute', {
      rows,
      source: options?.source || 'csv_import',
      skipDuplicates: options?.skipDuplicates ?? true,
      updateExisting: options?.updateExisting ?? false,
    });
    return data;
  },

  async importHistory(): Promise<{ history: KfsImportHistoryEntry[] }> {
    const { data } = await api.get('/kfs/import/history');
    return data;
  },

  async downloadImportTemplate(): Promise<void> {
    const res = await api.get('/kfs/import/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kfs-leads-szablon.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // === KFS Activity Stream ===

  async getActivityStream(params?: {
    limit?: number;
    offset?: number;
    action?: string;
    leadId?: number;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ activities: KfsStreamActivity[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.action) searchParams.set('action', params.action);
    if (params?.leadId) searchParams.set('leadId', String(params.leadId));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.dateFrom) searchParams.set('dateFrom', params.dateFrom);
    if (params?.dateTo) searchParams.set('dateTo', params.dateTo);
    const { data } = await api.get(`/kfs/activity/stream?${searchParams}`);
    return data;
  },

  async getActivityStats(): Promise<KfsActivityStats> {
    const { data } = await api.get('/kfs/activity/stats');
    return data;
  },

  // === KFS Notifications & Alerts ===

  async getNotifications(params?: {
    severity?: string; type?: string; limit?: number; include_dismissed?: boolean;
  }): Promise<{ notifications: KfsNotification[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.severity) qs.set('severity', params.severity);
    if (params?.type) qs.set('type', params.type);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.include_dismissed) qs.set('include_dismissed', 'true');
    const { data } = await api.get(`/kfs/notifications?${qs}`);
    return data;
  },

  async getNotificationCount(): Promise<{ unread: number }> {
    const { data } = await api.get('/kfs/notifications/count');
    return data;
  },

  async markNotificationRead(id: number | string): Promise<{ success: boolean }> {
    const { data } = await api.post(`/kfs/notifications/${id}/read`);
    return data;
  },

  async markAllNotificationsRead(): Promise<{ success: boolean; updated: number }> {
    const { data } = await api.post('/kfs/notifications/read-all');
    return data;
  },

  async dismissNotification(id: number | string): Promise<{ success: boolean }> {
    const { data } = await api.post(`/kfs/notifications/${id}/dismiss`);
    return data;
  },

  async generateNotifications(): Promise<{ created: number; skipped: number; total: number }> {
    const { data } = await api.post('/kfs/notifications/generate');
    return data;
  },

  // Scoring Tuner
  async getScoringConfig(): Promise<any> { const { data } = await api.get('/kfs/scoring/config'); return data; },
  async previewScoring(weights: any): Promise<any> { const { data } = await api.post('/kfs/scoring/preview', { weights }); return data; },
  async applyScoring(weights: any): Promise<any> { const { data } = await api.post('/kfs/scoring/apply', { weights }); return data; },

  // Compliance Checklist
  async getComplianceOverview(): Promise<any> { const { data } = await api.get('/kfs/compliance/overview'); return data; },
  async getComplianceForLead(leadId: number): Promise<any> { const { data } = await api.get(`/kfs/compliance/${leadId}`); return data; },
  async checkComplianceItem(leadId: number, itemId: string, checked: boolean): Promise<any> { const { data } = await api.post(`/kfs/compliance/${leadId}/check`, { itemId, checked }); return data; },
  async autoCheckCompliance(leadId: number): Promise<any> { const { data } = await api.post(`/kfs/compliance/${leadId}/auto-check`); return data; },

  // Enrollment Conversion
  async getConvertReady(): Promise<any> { const { data } = await api.get('/kfs/convert/ready'); return data; },
  async getConvertSessions(): Promise<any> { const { data } = await api.get('/kfs/convert/sessions'); return data; },
  async getConvertStats(): Promise<any> { const { data } = await api.get('/kfs/convert/stats'); return data; },
  async convertLead(leadId: number, body: any): Promise<any> { const { data } = await api.post(`/kfs/convert/${leadId}`, body); return data; },

  // Company 360
  async getCompany360(leadId: number): Promise<any> { const { data } = await api.get(`/kfs/company-360/${leadId}`); return data; },

  // R9: Bulk Operations
  async bulkUpdate(leadIds: number[], updates: any): Promise<any> { const { data } = await api.post('/kfs/bulk-update', { leadIds, updates }); return data; },
  async bulkDelete(leadIds: number[]): Promise<any> { const { data } = await api.post('/kfs/bulk-delete', { leadIds }); return data; },
  async exportLeads(params: { leadIds?: number[]; preset?: string; columns?: string[] }): Promise<any> { const { data } = await api.post('/kfs/export', params); return data; },

  // R9: Territory
  async getTerritoryStats(): Promise<any> { const { data } = await api.get('/kfs/territory/stats'); return data; },
  async assignTerritory(cities: string[], assignedTo: string): Promise<any> { const { data } = await api.post('/kfs/territory/assign', { cities, assignedTo }); return data; },

  // R9: Smart Scheduler
  async getSchedulerWeek(weekStart?: string): Promise<any> { const { data } = await api.get(`/kfs/scheduler/week${weekStart ? `?weekStart=${weekStart}` : ''}`); return data; },
  async scheduleContact(leadId: number, dateTime: string, notes?: string): Promise<any> { const { data } = await api.post('/kfs/scheduler/schedule', { leadId, dateTime, notes }); return data; },

  // R9: Follow-up Sequences
  async getSequences(): Promise<any[]> { const { data } = await api.get('/kfs/sequences'); return data; },
  async createSequence(seq: any): Promise<any> { const { data } = await api.post('/kfs/sequences', seq); return data; },
  async updateSequence(id: number, seq: any): Promise<any> { const { data } = await api.put(`/kfs/sequences/${id}`, seq); return data; },
  async deleteSequence(id: number): Promise<any> { const { data } = await api.delete(`/kfs/sequences/${id}`); return data; },

  // R9: AI Scoring
  async aiScore(leadIds?: number[]): Promise<any> { const { data } = await api.post('/kfs/scoring-ai/score', { leadIds }); return data; },
  async aiScoreExplain(leadId: number): Promise<any> { const { data } = await api.get(`/kfs/scoring-ai/explain/${leadId}`); return data; },

  // R10: Proposals (getProposals defined earlier in R6)
  async createProposal(body: any): Promise<any> { const { data } = await api.post('/kfs/proposals', body); return data; },
  async updateProposal(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/proposals/${id}`, body); return data; },
  async deleteProposal(id: number): Promise<any> { const { data } = await api.delete(`/kfs/proposals/${id}`); return data; },

  // R10: Meetings (getMeetings/createMeeting/updateMeeting/deleteMeeting/getMeetingStats defined here, R18 duplicates removed)
  async getMeetings(params?: any): Promise<any[]> { const q = new URLSearchParams(params || {}).toString(); const { data } = await api.get(`/kfs/meetings${q ? '?' + q : ''}`); return data; },
  async createMeeting(body: any): Promise<any> { const { data } = await api.post('/kfs/meetings', body); return data; },
  async updateMeeting(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/meetings/${id}`, body); return data; },
  async deleteMeeting(id: number): Promise<any> { const { data } = await api.delete(`/kfs/meetings/${id}`); return data; },
  async getMeetingStats(): Promise<any> { const { data } = await api.get('/kfs/meetings/stats'); return data; },

  // R10: Pipeline Analytics
  async getPipelineAnalytics(days?: number): Promise<any> { const { data } = await api.get(`/kfs/pipeline-analytics${days ? '?days=' + days : ''}`); return data; },

  // R11: System Overview
  async getSystemOverview(): Promise<any> { const { data } = await api.get('/kfs/system-overview'); return data; },

  // R11: Training Catalog
  async getCatalog(): Promise<any[]> { const { data } = await api.get('/kfs/catalog'); return data; },
  async addCatalogItem(body: any): Promise<any> { const { data } = await api.post('/kfs/catalog', body); return data; },
  async updateCatalogItem(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/catalog/${id}`, body); return data; },
  async assignTraining(leadId: number, catalogId: number, notes?: string): Promise<any> { const { data } = await api.post('/kfs/catalog/assign', { lead_id: leadId, catalog_id: catalogId, notes }); return data; },

  // R13: Contracts
  async getContracts(): Promise<any[]> { const { data } = await api.get('/kfs/contracts'); return data; },
  async createContract(body: any): Promise<any> { const { data } = await api.post('/kfs/contracts', body); return data; },
  async updateContract(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/contracts/${id}`, body); return data; },
  async deleteContract(id: number): Promise<any> { const { data } = await api.delete(`/kfs/contracts/${id}`); return data; },
  async getContractStats(): Promise<any> { const { data } = await api.get('/kfs/contracts/stats'); return data; },

  // R13: Financial
  async getFinancialOverview(): Promise<any> { const { data } = await api.get('/kfs/financial/overview'); return data; },

  // R13: Calendar
  async getKfsCalendar(month?: number, year?: number): Promise<any> { const params = new URLSearchParams(); if (month) params.set('month', String(month)); if (year) params.set('year', String(year)); const { data } = await api.get(`/kfs/calendar?${params}`); return data; },

  // R13: Team Workload
  async getTeamWorkload(): Promise<any> { const { data } = await api.get('/kfs/team-workload'); return data; },

  // R13: Funnel Optimizer
  async getFunnel(days?: number): Promise<any> { const { data } = await api.get(`/kfs/funnel${days ? '?days=' + days : ''}`); return data; },

  // R14: Document Vault
  async getDocumentVault(): Promise<any[]> { const { data } = await api.get('/kfs/documents-vault'); return data; },
  async createDocument(body: any): Promise<any> { const { data } = await api.post('/kfs/documents-vault', body); return data; },
  async updateDocument(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/documents-vault/${id}`, body); return data; },
  async deleteDocument(id: number): Promise<any> { const { data } = await api.delete(`/kfs/documents-vault/${id}`); return data; },
  async getDocumentVaultStats(): Promise<any> { const { data } = await api.get('/kfs/documents-vault/stats'); return data; },

  // R14: Lead Timeline
  async getLeadTimeline(leadId: number): Promise<any> { const { data } = await api.get(`/kfs/lead-timeline/${leadId}`); return data; },

  // R14: Nabor Comparison
  async getNaborComparison(): Promise<any> { const { data } = await api.get('/kfs/nabor-comparison'); return data; },

  // R14: Email Tracker
  async getEmailTracker(): Promise<any> { const { data } = await api.get('/kfs/email-tracker'); return data; },

  // R14: Region Heatmap
  async getRegionHeatmap(): Promise<any> { const { data } = await api.get('/kfs/region-heatmap'); return data; },

  // R15: Win/Loss Analysis
  async getWinLoss(days?: number): Promise<any> { const { data } = await api.get(`/kfs/win-loss${days ? '?days=' + days : ''}`); return data; },

  // R15: Contact Log
  async getContactLog(page?: number, limit?: number): Promise<any> { const { data } = await api.get(`/kfs/contact-log?page=${page || 1}&limit=${limit || 50}`); return data; },
  async createContactLog(body: any): Promise<any> { const { data } = await api.post('/kfs/contact-log', body); return data; },
  async getContactLogStats(): Promise<any> { const { data } = await api.get('/kfs/contact-log/stats'); return data; },

  // R15: Budget Planner
  async getBudget(): Promise<any> { const { data } = await api.get('/kfs/budget'); return data; },
  async createBudgetItem(body: any): Promise<any> { const { data } = await api.post('/kfs/budget', body); return data; },
  async updateBudgetItem(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/budget/${id}`, body); return data; },
  async deleteBudgetItem(id: number): Promise<any> { const { data } = await api.delete(`/kfs/budget/${id}`); return data; },

  // R15: Lead Scorecard
  async getLeadScorecard(leadId: number): Promise<any> { const { data } = await api.get(`/kfs/scorecard/${leadId}`); return data; },

  // R15: Campaign Manager
  async getCampaigns(): Promise<any[]> { const { data } = await api.get('/kfs/campaigns'); return data; },
  async createCampaign(body: any): Promise<any> { const { data } = await api.post('/kfs/campaigns', body); return data; },
  async updateCampaign(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/campaigns/${id}`, body); return data; },
  async deleteCampaign(id: number): Promise<any> { const { data } = await api.delete(`/kfs/campaigns/${id}`); return data; },
  async getCampaignStats(): Promise<any> { const { data } = await api.get('/kfs/campaigns/stats'); return data; },

  // R16: Invoices
  async getInvoices(): Promise<any[]> { const { data } = await api.get('/kfs/invoices'); return data; },
  async createInvoice(body: any): Promise<any> { const { data } = await api.post('/kfs/invoices', body); return data; },
  async updateInvoice(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/invoices/${id}`, body); return data; },
  async deleteInvoice(id: number): Promise<any> { const { data } = await api.delete(`/kfs/invoices/${id}`); return data; },
  async getInvoiceStats(): Promise<any> { const { data } = await api.get('/kfs/invoices/stats'); return data; },

  // R16: Trainers
  async getKfsTrainers(): Promise<any[]> { const { data } = await api.get('/kfs/trainers'); return data; },
  async createKfsTrainer(body: any): Promise<any> { const { data } = await api.post('/kfs/trainers', body); return data; },
  async updateKfsTrainer(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/trainers/${id}`, body); return data; },
  async deleteKfsTrainer(id: number): Promise<any> { const { data } = await api.delete(`/kfs/trainers/${id}`); return data; },

  // R16: PUP Directory
  async getPupDirectory(): Promise<any[]> { const { data } = await api.get('/kfs/pup-directory'); return data; },
  async createPupEntry(body: any): Promise<any> { const { data } = await api.post('/kfs/pup-directory', body); return data; },
  async updatePupEntry(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/pup-directory/${id}`, body); return data; },

  // R16: Deduplicator (mergeLeads defined earlier in Deduplication section)
  async getDeduplicator(): Promise<any> { const { data } = await api.get('/kfs/deduplicator'); return data; },

  // R16: Success Stories
  async getSuccessStories(): Promise<any[]> { const { data } = await api.get('/kfs/success-stories'); return data; },
  async createSuccessStory(body: any): Promise<any> { const { data } = await api.post('/kfs/success-stories', body); return data; },
  async updateSuccessStory(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/success-stories/${id}`, body); return data; },
  async deleteSuccessStory(id: number): Promise<any> { const { data } = await api.delete(`/kfs/success-stories/${id}`); return data; },

  // R17: Renewal Tracker
  async getRenewals(): Promise<any[]> { const { data } = await api.get('/kfs/renewals'); return data; },
  async createRenewal(body: any): Promise<any> { const { data } = await api.post('/kfs/renewals', body); return data; },
  async updateRenewal(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/renewals/${id}`, body); return data; },
  async deleteRenewal(id: number): Promise<any> { const { data } = await api.delete(`/kfs/renewals/${id}`); return data; },
  async getRenewalStats(): Promise<any> { const { data } = await api.get('/kfs/renewals/stats'); return data; },

  // R17: Competitor Intelligence
  async getCompetitors(): Promise<any[]> { const { data } = await api.get('/kfs/competitors'); return data; },
  async createCompetitor(body: any): Promise<any> { const { data } = await api.post('/kfs/competitors', body); return data; },
  async updateCompetitor(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/competitors/${id}`, body); return data; },
  async deleteCompetitor(id: number): Promise<any> { const { data } = await api.delete(`/kfs/competitors/${id}`); return data; },
  async getCompetitorStats(): Promise<any> { const { data } = await api.get('/kfs/competitors/stats'); return data; },

  // R17: ROI Calculator
  async getRoiCalculations(): Promise<any[]> { const { data } = await api.get('/kfs/roi-calculations'); return data; },
  async createRoiCalculation(body: any): Promise<any> { const { data } = await api.post('/kfs/roi-calculations', body); return data; },
  async updateRoiCalculation(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/roi-calculations/${id}`, body); return data; },
  async deleteRoiCalculation(id: number): Promise<any> { const { data } = await api.delete(`/kfs/roi-calculations/${id}`); return data; },
  async getRoiStats(): Promise<any> { const { data } = await api.get('/kfs/roi-calculations/stats'); return data; },

  // R17: Application Wizard
  async getKfsApplications(): Promise<any[]> { const { data } = await api.get('/kfs/applications'); return data; },
  async createKfsApplication(body: any): Promise<any> { const { data } = await api.post('/kfs/applications', body); return data; },
  async updateKfsApplication(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/applications/${id}`, body); return data; },
  async deleteKfsApplication(id: number): Promise<any> { const { data } = await api.delete(`/kfs/applications/${id}`); return data; },
  async getKfsApplicationStats(): Promise<any> { const { data } = await api.get('/kfs/applications/stats'); return data; },

  // R17: Partner Network
  async getPartners(): Promise<any[]> { const { data } = await api.get('/kfs/partners'); return data; },
  async createPartner(body: any): Promise<any> { const { data } = await api.post('/kfs/partners', body); return data; },
  async updatePartner(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/partners/${id}`, body); return data; },
  async deletePartner(id: number): Promise<any> { const { data } = await api.delete(`/kfs/partners/${id}`); return data; },
  async getPartnerStats(): Promise<any> { const { data } = await api.get('/kfs/partners/stats'); return data; },

  // R18: Goal Tracker
  async getGoals(): Promise<any[]> { const { data } = await api.get('/kfs/goals'); return data; },
  async createGoal(body: any): Promise<any> { const { data } = await api.post('/kfs/goals', body); return data; },
  async updateGoal(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/goals/${id}`, body); return data; },
  async deleteGoal(id: number): Promise<any> { const { data } = await api.delete(`/kfs/goals/${id}`); return data; },
  async getGoalStats(): Promise<any> { const { data } = await api.get('/kfs/goals/stats'); return data; },

  // R18: Market Analysis
  async getMarketAnalysis(): Promise<any[]> { const { data } = await api.get('/kfs/market-analysis'); return data; },
  async createMarketAnalysis(body: any): Promise<any> { const { data } = await api.post('/kfs/market-analysis', body); return data; },
  async updateMarketAnalysis(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/market-analysis/${id}`, body); return data; },
  async deleteMarketAnalysis(id: number): Promise<any> { const { data } = await api.delete(`/kfs/market-analysis/${id}`); return data; },
  async getMarketAnalysisStats(): Promise<any> { const { data } = await api.get('/kfs/market-analysis/stats'); return data; },

  // R18: Document Generator
  async getDocTemplates(): Promise<any[]> { const { data } = await api.get('/kfs/doc-templates'); return data; },
  async createDocTemplate(body: any): Promise<any> { const { data } = await api.post('/kfs/doc-templates', body); return data; },
  async updateDocTemplate(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/doc-templates/${id}`, body); return data; },
  async deleteDocTemplate(id: number): Promise<any> { const { data } = await api.delete(`/kfs/doc-templates/${id}`); return data; },
  async generateDocument(id: number, variables: Record<string, string>): Promise<any> { const { data } = await api.post(`/kfs/doc-templates/${id}/generate`, { variables }); return data; },
  async getDocTemplateStats(): Promise<any> { const { data } = await api.get('/kfs/doc-templates/stats'); return data; },

  // R18: Meeting Scheduler (duplicates removed — defined in R10 above)

  // R18: Referral Program
  async getReferrals(): Promise<any[]> { const { data } = await api.get('/kfs/referrals'); return data; },
  async createReferral(body: any): Promise<any> { const { data } = await api.post('/kfs/referrals', body); return data; },
  async updateReferral(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/referrals/${id}`, body); return data; },
  async deleteReferral(id: number): Promise<any> { const { data } = await api.delete(`/kfs/referrals/${id}`); return data; },
  async getReferralStats(): Promise<any> { const { data } = await api.get('/kfs/referrals/stats'); return data; },

  // R19: A/B Test Tracker
  async getAbTests(): Promise<any[]> { const { data } = await api.get('/kfs/ab-tests'); return data; },
  async createAbTest(body: any): Promise<any> { const { data } = await api.post('/kfs/ab-tests', body); return data; },
  async updateAbTest(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/ab-tests/${id}`, body); return data; },
  async deleteAbTest(id: number): Promise<any> { const { data } = await api.delete(`/kfs/ab-tests/${id}`); return data; },
  async getAbTestStats(): Promise<any> { const { data } = await api.get('/kfs/ab-tests/stats'); return data; },

  // R19: Churn Predictor
  async getChurnPredictions(): Promise<any[]> { const { data } = await api.get('/kfs/churn-predictions'); return data; },
  async createChurnPrediction(body: any): Promise<any> { const { data } = await api.post('/kfs/churn-predictions', body); return data; },
  async updateChurnPrediction(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/churn-predictions/${id}`, body); return data; },
  async deleteChurnPrediction(id: number): Promise<any> { const { data } = await api.delete(`/kfs/churn-predictions/${id}`); return data; },
  async getChurnStats(): Promise<any> { const { data } = await api.get('/kfs/churn-predictions/stats'); return data; },

  // R19: Integration Hub
  async getIntegrations(): Promise<any[]> { const { data } = await api.get('/kfs/integrations'); return data; },
  async createIntegration(body: any): Promise<any> { const { data } = await api.post('/kfs/integrations', body); return data; },
  async updateIntegration(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/integrations/${id}`, body); return data; },
  async deleteIntegration(id: number): Promise<any> { const { data } = await api.delete(`/kfs/integrations/${id}`); return data; },
  async getIntegrationStats(): Promise<any> { const { data } = await api.get('/kfs/integrations/stats'); return data; },

  // R19: Satisfaction Survey
  async getSurveys(): Promise<any[]> { const { data } = await api.get('/kfs/surveys'); return data; },
  async createSurvey(body: any): Promise<any> { const { data } = await api.post('/kfs/surveys', body); return data; },
  async updateSurvey(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/surveys/${id}`, body); return data; },
  async deleteSurvey(id: number): Promise<any> { const { data } = await api.delete(`/kfs/surveys/${id}`); return data; },
  async getSurveyStats(): Promise<any> { const { data } = await api.get('/kfs/surveys/stats'); return data; },

  // R19: Quotation Builder
  async getQuotations(): Promise<any[]> { const { data } = await api.get('/kfs/quotations'); return data; },
  async createQuotation(body: any): Promise<any> { const { data } = await api.post('/kfs/quotations', body); return data; },
  async updateQuotation(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/quotations/${id}`, body); return data; },
  async deleteQuotation(id: number): Promise<any> { const { data } = await api.delete(`/kfs/quotations/${id}`); return data; },
  async getQuotationStats(): Promise<any> { const { data } = await api.get('/kfs/quotations/stats'); return data; },

  // R20: Webhook Manager
  async getWebhooks(): Promise<any[]> { const { data } = await api.get('/kfs/webhooks'); return data; },
  async createWebhook(body: any): Promise<any> { const { data } = await api.post('/kfs/webhooks', body); return data; },
  async updateWebhook(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/webhooks/${id}`, body); return data; },
  async deleteWebhook(id: number): Promise<any> { const { data } = await api.delete(`/kfs/webhooks/${id}`); return data; },
  async getWebhookStats(): Promise<any> { const { data } = await api.get('/kfs/webhooks/stats'); return data; },

  // R20: Nabor Calendar
  async getNaborCalendar(): Promise<any[]> { const { data } = await api.get('/kfs/nabor-calendar'); return data; },
  async createNaborEntry(body: any): Promise<any> { const { data } = await api.post('/kfs/nabor-calendar', body); return data; },
  async updateNaborEntry(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/nabor-calendar/${id}`, body); return data; },
  async deleteNaborEntry(id: number): Promise<any> { const { data } = await api.delete(`/kfs/nabor-calendar/${id}`); return data; },
  async getNaborCalendarStats(): Promise<any> { const { data } = await api.get('/kfs/nabor-calendar/stats'); return data; },

  // R20: Company Profile
  async getCompanyProfiles(): Promise<any[]> { const { data } = await api.get('/kfs/company-profiles'); return data; },
  async createCompanyProfile(body: any): Promise<any> { const { data } = await api.post('/kfs/company-profiles', body); return data; },
  async updateCompanyProfile(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/company-profiles/${id}`, body); return data; },
  async deleteCompanyProfile(id: number): Promise<any> { const { data } = await api.delete(`/kfs/company-profiles/${id}`); return data; },
  async getCompanyProfileStats(): Promise<any> { const { data } = await api.get('/kfs/company-profiles/stats'); return data; },

  // R20: Training Catalog
  async getTrainingCatalog(): Promise<any[]> { const { data } = await api.get('/kfs/training-catalog'); return data; },
  async createTrainingCatalogItem(body: any): Promise<any> { const { data } = await api.post('/kfs/training-catalog', body); return data; },
  async updateTrainingCatalogItem(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/training-catalog/${id}`, body); return data; },
  async deleteTrainingCatalogItem(id: number): Promise<any> { const { data } = await api.delete(`/kfs/training-catalog/${id}`); return data; },
  async getTrainingCatalogStats(): Promise<any> { const { data } = await api.get('/kfs/training-catalog/stats'); return data; },

  // R20: Sales Scripts
  async getSalesScripts(): Promise<any[]> { const { data } = await api.get('/kfs/sales-scripts'); return data; },
  async createSalesScript(body: any): Promise<any> { const { data } = await api.post('/kfs/sales-scripts', body); return data; },
  async updateSalesScript(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/sales-scripts/${id}`, body); return data; },
  async deleteSalesScript(id: number): Promise<any> { const { data } = await api.delete(`/kfs/sales-scripts/${id}`); return data; },
  async getSalesScriptStats(): Promise<any> { const { data } = await api.get('/kfs/sales-scripts/stats'); return data; },

  // R21: Events
  async getEvents(): Promise<any[]> { const { data } = await api.get('/kfs/events'); return data; },
  async createEvent(body: any): Promise<any> { const { data } = await api.post('/kfs/events', body); return data; },
  async updateEvent(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/events/${id}`, body); return data; },
  async deleteEvent(id: number): Promise<any> { const { data } = await api.delete(`/kfs/events/${id}`); return data; },
  async getEventStats(): Promise<any> { const { data } = await api.get('/kfs/events/stats'); return data; },

  // R21: Audit Log
  async getAuditLog(limit?: number, offset?: number): Promise<any[]> { const { data } = await api.get(`/kfs/audit-log?limit=${limit||100}&offset=${offset||0}`); return data; },
  async createAuditEntry(body: any): Promise<any> { const { data } = await api.post('/kfs/audit-log', body); return data; },
  async deleteAuditEntry(id: number): Promise<any> { const { data } = await api.delete(`/kfs/audit-log/${id}`); return data; },
  async getAuditLogStats(): Promise<any> { const { data } = await api.get('/kfs/audit-log/stats'); return data; },

  // R21: Pricing Strategy
  async getPricing(): Promise<any[]> { const { data } = await api.get('/kfs/pricing'); return data; },
  async createPricing(body: any): Promise<any> { const { data } = await api.post('/kfs/pricing', body); return data; },
  async updatePricing(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/pricing/${id}`, body); return data; },
  async deletePricing(id: number): Promise<any> { const { data } = await api.delete(`/kfs/pricing/${id}`); return data; },
  async getPricingStats(): Promise<any> { const { data } = await api.get('/kfs/pricing/stats'); return data; },

  // R21: Client Feedback
  async getClientFeedback(): Promise<any[]> { const { data } = await api.get('/kfs/client-feedback'); return data; },
  async createClientFeedback(body: any): Promise<any> { const { data } = await api.post('/kfs/client-feedback', body); return data; },
  async updateClientFeedback(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/client-feedback/${id}`, body); return data; },
  async deleteClientFeedback(id: number): Promise<any> { const { data } = await api.delete(`/kfs/client-feedback/${id}`); return data; },
  async getClientFeedbackStats(): Promise<any> { const { data } = await api.get('/kfs/client-feedback/stats'); return data; },

  // R21: Certificates
  async getCertificates(): Promise<any[]> { const { data } = await api.get('/kfs/certificates'); return data; },
  async createCertificate(body: any): Promise<any> { const { data } = await api.post('/kfs/certificates', body); return data; },
  async updateCertificate(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/certificates/${id}`, body); return data; },
  async deleteCertificate(id: number): Promise<any> { const { data } = await api.delete(`/kfs/certificates/${id}`); return data; },
  async getCertificateStats(): Promise<any> { const { data } = await api.get('/kfs/certificates/stats'); return data; },

  // R22: Trainers
  async getTrainers(): Promise<any[]> { const { data } = await api.get('/kfs/trainers'); return data; },
  async createTrainer(body: any): Promise<any> { const { data } = await api.post('/kfs/trainers', body); return data; },
  async updateTrainer(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/trainers/${id}`, body); return data; },
  async deleteTrainer(id: number): Promise<any> { const { data } = await api.delete(`/kfs/trainers/${id}`); return data; },
  async getTrainerStats(): Promise<any> { const { data } = await api.get('/kfs/trainers/stats'); return data; },

  // R22: Revenue Forecasts
  async getRevenueForecasts(): Promise<any[]> { const { data } = await api.get('/kfs/revenue-forecasts'); return data; },
  async createRevenueForecast(body: any): Promise<any> { const { data } = await api.post('/kfs/revenue-forecasts', body); return data; },
  async updateRevenueForecast(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/revenue-forecasts/${id}`, body); return data; },
  async deleteRevenueForecast(id: number): Promise<any> { const { data } = await api.delete(`/kfs/revenue-forecasts/${id}`); return data; },
  async getRevenueForecastStats(): Promise<any> { const { data } = await api.get('/kfs/revenue-forecasts/stats'); return data; },

  // R22: Email Sequences
  async getEmailSequences(): Promise<any[]> { const { data } = await api.get('/kfs/email-sequences'); return data; },
  async createEmailSequence(body: any): Promise<any> { const { data } = await api.post('/kfs/email-sequences', body); return data; },
  async updateEmailSequence(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/email-sequences/${id}`, body); return data; },
  async deleteEmailSequence(id: number): Promise<any> { const { data } = await api.delete(`/kfs/email-sequences/${id}`); return data; },
  async getEmailSequenceStats(): Promise<any> { const { data } = await api.get('/kfs/email-sequences/stats'); return data; },

  // R22: Data Exports
  async getDataExports(): Promise<any[]> { const { data } = await api.get('/kfs/data-exports'); return data; },
  async createDataExport(body: any): Promise<any> { const { data } = await api.post('/kfs/data-exports', body); return data; },
  async updateDataExport(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/data-exports/${id}`, body); return data; },
  async deleteDataExport(id: number): Promise<any> { const { data } = await api.delete(`/kfs/data-exports/${id}`); return data; },
  async getDataExportStats(): Promise<any> { const { data } = await api.get('/kfs/data-exports/stats'); return data; },

  // R22: PUP Relationships
  async getPupRelationships(): Promise<any[]> { const { data } = await api.get('/kfs/pup-relationships'); return data; },
  async createPupRelationship(body: any): Promise<any> { const { data } = await api.post('/kfs/pup-relationships', body); return data; },
  async updatePupRelationship(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/pup-relationships/${id}`, body); return data; },
  async deletePupRelationship(id: number): Promise<any> { const { data } = await api.delete(`/kfs/pup-relationships/${id}`); return data; },
  async getPupRelationshipStats(): Promise<any> { const { data } = await api.get('/kfs/pup-relationships/stats'); return data; },

  // R23: Onboarding
  async getOnboarding(): Promise<any[]> { const { data } = await api.get('/kfs/onboarding'); return data; },
  async createOnboarding(body: any): Promise<any> { const { data } = await api.post('/kfs/onboarding', body); return data; },
  async updateOnboarding(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/onboarding/${id}`, body); return data; },
  async deleteOnboarding(id: number): Promise<any> { const { data } = await api.delete(`/kfs/onboarding/${id}`); return data; },
  async getOnboardingStats(): Promise<any> { const { data } = await api.get('/kfs/onboarding/stats'); return data; },

  // R23: Document Checklist
  async getDocChecklist(): Promise<any[]> { const { data } = await api.get('/kfs/doc-checklist'); return data; },
  async createDocChecklist(body: any): Promise<any> { const { data } = await api.post('/kfs/doc-checklist', body); return data; },
  async updateDocChecklist(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/doc-checklist/${id}`, body); return data; },
  async deleteDocChecklist(id: number): Promise<any> { const { data } = await api.delete(`/kfs/doc-checklist/${id}`); return data; },
  async getDocChecklistStats(): Promise<any> { const { data } = await api.get('/kfs/doc-checklist/stats'); return data; },

  // R23: Compliance Calendar
  async getComplianceCalendar(): Promise<any[]> { const { data } = await api.get('/kfs/compliance-calendar'); return data; },
  async createComplianceEvent(body: any): Promise<any> { const { data } = await api.post('/kfs/compliance-calendar', body); return data; },
  async updateComplianceEvent(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/compliance-calendar/${id}`, body); return data; },
  async deleteComplianceEvent(id: number): Promise<any> { const { data } = await api.delete(`/kfs/compliance-calendar/${id}`); return data; },
  async getComplianceCalendarStats(): Promise<any> { const { data } = await api.get('/kfs/compliance-calendar/stats'); return data; },

  // R23: Team Performance
  async getTeamPerformance(): Promise<any[]> { const { data } = await api.get('/kfs/team-performance'); return data; },
  async createTeamPerformance(body: any): Promise<any> { const { data } = await api.post('/kfs/team-performance', body); return data; },
  async updateTeamPerformance(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/team-performance/${id}`, body); return data; },
  async deleteTeamPerformance(id: number): Promise<any> { const { data } = await api.delete(`/kfs/team-performance/${id}`); return data; },
  async getTeamPerformanceStats(): Promise<any> { const { data } = await api.get('/kfs/team-performance/stats'); return data; },

  // R23: Marketing Campaigns
  async getMarketingCampaigns(): Promise<any[]> { const { data } = await api.get('/kfs/marketing-campaigns'); return data; },
  async createMarketingCampaign(body: any): Promise<any> { const { data } = await api.post('/kfs/marketing-campaigns', body); return data; },
  async updateMarketingCampaign(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/marketing-campaigns/${id}`, body); return data; },
  async deleteMarketingCampaign(id: number): Promise<any> { const { data } = await api.delete(`/kfs/marketing-campaigns/${id}`); return data; },
  async getMarketingCampaignStats(): Promise<any> { const { data } = await api.get('/kfs/marketing-campaigns/stats'); return data; },

  // R24: Call Log
  async getCallLog(): Promise<any[]> { const { data } = await api.get('/kfs/call-log'); return data; },
  async createCallLog(body: any): Promise<any> { const { data } = await api.post('/kfs/call-log', body); return data; },
  async updateCallLog(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/call-log/${id}`, body); return data; },
  async deleteCallLog(id: number): Promise<any> { const { data } = await api.delete(`/kfs/call-log/${id}`); return data; },
  async getCallLogStats(): Promise<any> { const { data } = await api.get('/kfs/call-log/stats'); return data; },

  // R24: Content Library
  async getContentLibrary(): Promise<any[]> { const { data } = await api.get('/kfs/content-library'); return data; },
  async createContent(body: any): Promise<any> { const { data } = await api.post('/kfs/content-library', body); return data; },
  async updateContent(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/content-library/${id}`, body); return data; },
  async deleteContent(id: number): Promise<any> { const { data } = await api.delete(`/kfs/content-library/${id}`); return data; },
  async getContentLibraryStats(): Promise<any> { const { data } = await api.get('/kfs/content-library/stats'); return data; },

  // R24: Deadlines
  async getDeadlines(): Promise<any[]> { const { data } = await api.get('/kfs/deadlines'); return data; },
  async createDeadline(body: any): Promise<any> { const { data } = await api.post('/kfs/deadlines', body); return data; },
  async updateDeadline(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/deadlines/${id}`, body); return data; },
  async deleteDeadline(id: number): Promise<any> { const { data } = await api.delete(`/kfs/deadlines/${id}`); return data; },
  async getDeadlineStats(): Promise<any> { const { data } = await api.get('/kfs/deadlines/stats'); return data; },

  // R24: Custom Reports
  async getCustomReports(): Promise<any[]> { const { data } = await api.get('/kfs/custom-reports'); return data; },
  async createCustomReport(body: any): Promise<any> { const { data } = await api.post('/kfs/custom-reports', body); return data; },
  async updateCustomReport(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/custom-reports/${id}`, body); return data; },
  async deleteCustomReport(id: number): Promise<any> { const { data } = await api.delete(`/kfs/custom-reports/${id}`); return data; },
  async getCustomReportStats(): Promise<any> { const { data } = await api.get('/kfs/custom-reports/stats'); return data; },

  // R24: Workflows
  async getWorkflows(): Promise<any[]> { const { data } = await api.get('/kfs/workflows'); return data; },
  async createWorkflow(body: any): Promise<any> { const { data } = await api.post('/kfs/workflows', body); return data; },
  async updateWorkflow(id: number, body: any): Promise<any> { const { data } = await api.put(`/kfs/workflows/${id}`, body); return data; },
  async deleteWorkflow(id: number): Promise<any> { const { data } = await api.delete(`/kfs/workflows/${id}`); return data; },
  async getWorkflowStats(): Promise<any> { const { data } = await api.get('/kfs/workflows/stats'); return data; },
};

// KFS Notification types
export type KfsNotificationType = 'pup_deadline' | 'overdue_followup' | 'hot_lead' | 'automation' | 'system' | 'lead_converted' | 'duplicate_found';
export type KfsNotificationSeverity = 'critical' | 'warning' | 'info' | 'success';

export interface KfsNotification {
  id: number | string;
  user_id?: number;
  type: KfsNotificationType;
  title: string;
  message: string | null;
  severity: KfsNotificationSeverity;
  related_lead_id?: number | null;
  related_nabor_id?: number | null;
  action_url?: string | null;
  is_read: boolean;
  is_dismissed?: boolean;
  is_realtime?: boolean;
  created_at: string;
}

// KFS Activity Stream types
export interface KfsStreamActivity {
  id: number;
  lead_id: number | null;
  action: string;
  details: any;
  performed_by: number | string | null;
  created_at: string;
  company_name: string | null;
  nip: string | null;
  city: string | null;
}

export interface KfsActivityStats {
  total: number;
  today: number;
  byAction: Array<{ action: string; count: string }>;
  byUser: Array<{ performed_by: string; count: string }>;
  topLeads: Array<{ lead_id: number; company_name: string | null; activity_count: string }>;
}

// KFS Email Queue types
export interface KfsQueuedEmail {
  id: number;
  lead_id: number | null;
  template_id: number | null;
  to_email: string;
  to_name: string;
  company_name: string;
  subject: string;
  body: string;
  status: 'draft' | 'queued' | 'approved' | 'sent' | 'failed';
  created_by: number | null;
  approved_by: number | null;
  created_at: string;
  approved_at: string | null;
  sent_at: string | null;
  metadata: {
    template_name?: string;
    template_stage?: string;
    variables_used?: Record<string, string>;
    missing_variables?: string[];
  };
}

export interface KfsEmailQueueStats {
  total: number;
  drafts: number;
  queued: number;
  approved: number;
  sent: number;
  failed: number;
  unique_leads: number;
  templates_used: number;
  oldest: string | null;
  newest: string | null;
  by_day: { day: string; status: string; cnt: number }[];
}

// KFS Reporting Engine types
export interface KfsReportTypeInfo {
  id: string;
  name: string;
  description: string;
  supports_csv: boolean;
  supports_date_filter: boolean;
}

// KFS PUP Automation types
export interface KfsPupOfficeInfo {
  id: string;
  name: string;
  city: string;
  url: string;
  phone?: string;
  powiat: string;
}

export interface KfsPupNabor {
  id: number;
  pup_id: string;
  pup_name: string;
  year: number;
  round: number;
  start_date: string;
  end_date: string;
  budget: number | null;
  priorities: string[];
  status: 'upcoming' | 'active' | 'closed' | 'results';
  notes: string | null;
  leads_assigned: number;
  applications_submitted: number;
  created_at: string;
  updated_at: string;
}

export interface KfsPupNaborLead {
  id: number;
  nabor_id: number;
  lead_id: number;
  status: string;
  application_date: string | null;
  result_date: string | null;
  notes: string | null;
  created_at: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  nip?: string;
  company_city?: string;
  lead_score?: number | null;
  training_area?: string;
}

export interface KfsPupDeadlineAlert {
  nabor_id: number;
  pup_name: string;
  pup_id: string;
  end_date: string;
  days_remaining: number;
  severity: 'critical' | 'warning' | 'info';
  unsubmitted_leads: number;
  total_leads: number;
}

export interface KfsPupNaborTimeline {
  months: Array<{
    month: string;
    nabory: Array<{
      id: number;
      pup_id: string;
      pup_name: string;
      start_date: string;
      end_date: string;
      status: string;
      leads_count: number;
      budget: number | null;
      round: number;
    }>;
  }>;
}

export interface KfsPupAutomationStats {
  total_nabory: number;
  active_nabory: number;
  upcoming_nabory: number;
  closed_nabory: number;
  total_budget: number;
  total_leads_assigned: number;
  total_applications_submitted: number;
  alerts_count: number;
}

// KFS Calendar types
export interface KfsCalendarEvent {
  id: number | string;
  type: 'nabor' | 'session' | 'deadline' | 'followup' | 'training' | 'reminder';
  title: string;
  date: string;
  endDate?: string;
  description: string;
  voivodeship?: string;
  color: string;
  priority: 'high' | 'medium' | 'low';
  url?: string;
  metadata?: Record<string, any>;
}

export interface KfsCalendarNabor {
  id: string;
  pup_name: string;
  start_date: string;
  end_date: string;
  notes?: string;
  budget_amount?: number;
}

// KFS Handlowiec Dashboard types
export interface KfsHandlowiecLead {
  id: number;
  company_name: string;
  nip: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_city: string;
  powiat: string;
  status: string;
  lead_score: number | null;
  training_area: string;
  assigned_to: string | null;
  follow_up_date: string | null;
  last_contact_date: string | null;
  contact_count: number;
  created_at: string;
  updated_at: string;
}

export interface KfsHandlowiecDashboardData {
  stats: {
    total: number;
    byStatus: Record<string, number>;
    byCity: Record<string, number>;
    contactedToday: number;
    conversionsThisMonth: number;
  };
  todayTasks: KfsHandlowiecLead[];
  recentLeads: KfsHandlowiecLead[];
  pipeline: Record<string, number>;
  ranking: Array<{ name: string; contacted: number; conversions: number }>;
}

// KFS Wniosek Document type
export interface KfsWniosekDocument {
  meta: {
    generated_at: string;
    generated_by: string;
    template: string;
    lead_id: number;
    session_id: number | null;
    version: number;
  };
  dane_firmy: {
    nazwa: string;
    nip: string;
    regon: string;
    adres: string;
    kod_pocztowy: string;
    miasto: string;
    pkd: string[];
    wielkosc_firmy: string;
    wielkosc_firmy_label: string;
    liczba_pracownikow: number | null;
    osoba_kontaktowa: string;
    email: string;
    telefon: string;
    hr_kontakt: string | null;
    hr_email: string | null;
    hr_telefon: string | null;
  };
  dane_uczestnikow: Array<{
    lp: number;
    imie: string;
    nazwisko: string;
    stanowisko: string;
    pesel: string;
    wiek_45_plus: boolean;
    niepelnosprawnosc: boolean;
    cudzoziemiec: boolean;
  }>;
  dane_szkolenia: {
    nazwa: string;
    kod_szkolenia: string | null;
    zakres_kfs: string;
    kategoria: string | null;
    opis: string | null;
    liczba_godzin: number | null;
    forma: string;
    data_rozpoczecia: string | null;
    data_zakonczenia: string | null;
    miejsce: string | null;
    dostawca: string;
    numer_ris: string | null;
  };
  kalkulacja_kosztow: {
    cost_per_person: number;
    participants_count: number;
    total_cost: number;
    company_size: string;
    kfs_percent: number;
    own_percent: number;
    kfs_amount: number;
    own_amount: number;
    max_kfs_per_person: number;
    exceeds_limit: boolean;
  };
  priorytety_kfs: {
    rok: number;
    dopasowane: string[];
    wszystkie: string[];
  };
  uzasadnienie: string | null;
  powiat: string;
  pup_office_id: number | null;
}

// KFS Overlap Detection API (separate export for TS inference limit workaround)
export const kfsOverlapApi = {
  async checkOverlap(params: { pesel?: string; email?: string; firstName?: string; lastName?: string; kfsZakres: string; excludeSessionId?: number }): Promise<any> {
    const { data } = await api.post('/kfs/overlap-check', params);
    return data;
  },
  async checkOverlapLead(leadId: number): Promise<any> {
    const { data } = await api.post('/kfs/overlap-check-lead', { leadId });
    return data;
  },
  async getParticipantHistory(participantId: number): Promise<any> {
    const { data } = await api.get(`/kfs/participant-history/${participantId}`);
    return data;
  },
  async getCompanyHistory(nip: string): Promise<any> {
    const { data } = await api.get(`/kfs/company-history/${nip}`);
    return data;
  },
  async getLeadHistory(leadId: number): Promise<any> {
    const { data } = await api.get(`/kfs/lead-history/${leadId}`);
    return data;
  },
  async getComplianceReport(): Promise<any> {
    const { data } = await api.get('/kfs/compliance-report');
    return data;
  },
  async downloadComplianceCsv(): Promise<void> {
    const { data } = await api.get('/kfs/compliance-report/csv', { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `kfs-compliance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
  async getZakresLabels(): Promise<Record<string, string>> {
    const { data } = await api.get('/kfs/zakres-labels');
    return data;
  },
  async getExecutiveKpi(): Promise<any> {
    const { data } = await api.get('/kfs/executive-kpi');
    return data;
  },
  async getParticipantCard(enrollmentId: number): Promise<KfsParticipantCardData> {
    const { data } = await api.get(`/kfs/participant-card/${enrollmentId}`);
    return data;
  },
  async getCompanyProfile(nip: string): Promise<KfsCompanyProfileData> {
    const { data } = await api.get(`/kfs/company/${nip}`);
    return data;
  },
};

// KFS Participant Card types
export interface KfsParticipantCardData {
  participant: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    pesel_masked: string | null;
    email: string | null;
    phone: string | null;
  };
  current_enrollment: {
    id: number;
    session_id: number;
    session_code: string;
    course_name: string;
    course_code: string;
    course_category: string | null;
    kfs_zakres: string | null;
    kfs_zakres_normalized: string | null;
    kfs_zakres_label: string | null;
    start_date: string | null;
    end_date: string | null;
    location: string | null;
    duration_hours: number | null;
    funding_source: string;
    funding_amount: number | null;
    status: string;
    employer_name: string | null;
    employer_nip: string | null;
    created_at: string;
  };
  kfs_history: KfsHistoryEntry[];
  company: {
    name: string | null;
    nip: string | null;
    total_kfs_enrollments: number;
    employees: KfsCompanyEmployee[];
  };
  overlap: {
    has_conflicts: boolean;
    severity: 'block' | 'warning' | 'info' | 'none';
    matches: any[];
    checked_zakres: string;
  } | null;
  stats: {
    total_trainings: number;
    unique_zakresy: number;
    completed_zakresy: number;
    first_kfs_date: string | null;
    last_kfs_date: string | null;
  };
  zakres_labels: Record<string, string>;
}

export interface KfsHistoryEntry {
  source: 'enrollment' | 'crm_application' | 'kfs_lead';
  source_id: number;
  first_name: string | null;
  last_name: string | null;
  pesel: string | null;
  email: string | null;
  kfs_zakres: string | null;
  kfs_zakres_normalized: string | null;
  kfs_zakres_label: string | null;
  course_name: string | null;
  session_code: string | null;
  status: string | null;
  funding_source: string;
  created_at: string;
  employer_name: string | null;
  employer_nip: string | null;
}

export interface KfsCompanyEmployee {
  name: string;
  email: string | null;
  kfs_zakres: string | null;
  kfs_zakres_normalized: string | null;
  source: string;
  date: string;
}

// KFS Company Profile types ("Firma 360°")
export interface KfsCompanyProfileData {
  company: {
    name: string;
    nip: string;
    regon: string | null;
    address: string | null;
    pkd: string | null;
    size: string | null;
    first_contact: string | null;
    source: string;
  };
  kfs_summary: {
    total_applications: number;
    total_approved: number;
    total_funding_pln: number;
    total_participants: number;
    zakres_coverage: string[];
    zakres_available: string[];
    compliance_status: 'ok' | 'warning' | 'blocked';
    overlap_conflicts: number;
  };
  leads: Array<{
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    company_name: string;
    nip: string;
    training_area: string | null;
    training_area_normalized: string | null;
    status: string;
    lead_score: number | null;
    assigned_to: string | null;
    created_at: string;
    powiat: string | null;
  }>;
  applications: Array<{
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    company_name: string;
    nip: string;
    training_area: string | null;
    training_area_normalized: string | null;
    status: string;
    ai_score: number | null;
    matched_priority: number | null;
    created_at: string;
  }>;
  enrollments: Array<{
    id: number;
    session_id: number;
    session_code: string;
    course_name: string;
    course_code: string;
    course_category: string | null;
    kfs_zakres: string | null;
    kfs_zakres_normalized: string | null;
    participant_name: string;
    participant_email: string | null;
    status: string;
    funding_amount: number | null;
    start_date: string | null;
    end_date: string | null;
    duration_hours: number | null;
    enrollment_date: string;
  }>;
  timeline: Array<{
    date: string;
    type: 'lead' | 'application' | 'enrollment' | 'completion';
    title: string;
    details: string;
    source_id: number;
    status: string;
  }>;
  participants: Array<{
    id: number;
    name: string;
    email: string | null;
    pesel_last4: string | null;
    enrollments_count: number;
    zakres_list: string[];
  }>;
  zakres_labels: Record<string, string>;
}

export const KFS_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  applied: { label: 'Wniosek złożony', color: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Zatwierdźony', color: 'bg-green-100 text-green-700' },
  in_training: { label: 'W trakcie szkolenia', color: 'bg-amber-100 text-amber-700' },
  settled: { label: 'Rozliczony', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Odrzucony', color: 'bg-red-100 text-red-700' },
};

export const NABOR_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  unknown: { label: 'Brak danych', color: 'bg-gray-100 text-gray-600' },
  planned: { label: 'Planowany', color: 'bg-blue-100 text-blue-700' },
  open: { label: 'Otwarty', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Zamknięty', color: 'bg-red-100 text-red-700' },
  suspended: { label: 'Wstrzymany', color: 'bg-amber-100 text-amber-700' },
};

// ─── KFS Firm Intelligence Types ────────────────────────────────────

export interface KfsFirmProfile {
  nip: string;
  company_name: string;
  pkd_main: string;
  pkd_all: string[];
  employee_range: string;
  website_url: string | null;
  website_active: boolean;
  has_ssl: boolean;
  website_tech: string[];
  facebook_url: string | null;
  linkedin_url: string | null;
  instagram_url: string | null;
  google_maps_url: string | null;
  google_rating: number | null;
  google_reviews_count: number | null;
  emails_found: string[];
  phones_found: string[];
  is_active_online: boolean;
  digital_maturity_score: number;
  training_potential: string[];
  estimated_revenue_range: string | null;
  last_enriched: string;
  enrichment_sources: string[];
}

export interface KfsWebsiteAnalysis {
  active: boolean;
  has_ssl: boolean;
  title: string | null;
  description: string | null;
  tech_detected: string[];
  emails_found: string[];
  phones_found: string[];
  social_links: {
    facebook?: string;
    linkedin?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
  };
}

export interface KfsDigitalMaturityResult {
  score: number;
  breakdown: Record<string, number>;
  recommendations: string[];
}

export interface KfsBulkIntelligenceResult {
  total_processed: number;
  profiles_built: number;
  websites_found: number;
  social_found: number;
  avg_digital_maturity: number;
  errors: number;
}

export interface KfsFirmIntelligenceStats {
  total_leads: number;
  with_website: number;
  with_facebook: number;
  with_linkedin: number;
  with_google_maps: number;
  avg_digital_maturity: number;
  by_maturity_level: Array<{ level: string; count: number }>;
  training_demand: Array<{ area: string; count: number }>;
}

export interface KfsTrainingDemandResult {
  demand: Array<{ code: string; label: string; count: number; percentage: number }>;
  total_profiled: number;
  avg_suggestions_per_lead: number;
}

export const DIGITAL_MATURITY_LABELS: Record<string, { label: string; color: string }> = {
  advanced: { label: 'Zaawansowany (80-100)', color: 'bg-green-100 text-green-700' },
  medium: { label: 'Średni (50-79)', color: 'bg-blue-100 text-blue-700' },
  low: { label: 'Niski (20-49)', color: 'bg-amber-100 text-amber-700' },
  very_low: { label: 'Bardzo niski (0-19)', color: 'bg-red-100 text-red-700' },
};

export const TRAINING_SUGGESTION_LABELS: Record<string, string> = {
  cyfrowe_podstawowe: 'Kompetencje cyfrowe - podstawowe',
  cyfrowe_zaawansowane: 'Kompetencje cyfrowe - zaawansowane (AI, automatyzacja)',
  social_media: 'Social media i marketing cyfrowy',
  ai_machine_learning: 'Sztuczna inteligencja i uczenie maszynowe',
  cyberbezpieczenstwo: 'Cyberbezpieczeństwo',
  obsluga_klienta: 'Obsługa klienta',
  jezyki_obce: 'Języki obce',
  bhp: 'BHP i bezpieczeństwo pracy',
  techniczne: 'Szkolenia techniczne branżowe',
  lean_manufacturing: 'Lean Manufacturing / Six Sigma',
  finanse_rachunkowosc: 'Finanse i rachunkowość',
  compliance: 'Compliance i regulacje',
  medyczne: 'Szkolenia medyczne',
  ochrona_danych_osobowych: 'Ochrona danych osobowych (RODO)',
  metodyka_szkoleniowa: 'Metodyka szkoleniowa',
  sprzedaz_marketing: 'Sprzedaż i marketing',
  e_commerce: 'E-commerce',
  logistyka: 'Logistyka i łańcuch dostaw',
  zarzadzanie_zespolem: 'Zarządzanie zespołem',
  zarzadzanie_projektami: 'Zarządzanie projektami',
  hr_kadry: 'HR i kadry',
};

// ─── KFS Kanban Board Types ────────────────────────────────────

export interface KfsKanbanLead {
  id: number;
  company_name: string;
  nip: string | null;
  company_city: string | null;
  status: string;
  lead_score: number | null;
  pkd_main: string | null;
  email: string | null;
  phone: string | null;
  assigned_to: string | null;
  last_contact_date: string | null;
  follow_up_date: string | null;
  training_area: string | null;
  data_source: string | null;
  created_at: string;
  updated_at: string;
  first_name: string | null;
  last_name: string | null;
  company_address: string | null;
  company_size: string | null;
  employee_count: number | null;
  voivodeship: string | null;
  tags: string[] | null;
  notes: string | null;
  contact_count: number | null;
  powiat: string | null;
}

export interface KfsKanbanColumnStat {
  status: string;
  count: number;
  pipeline_value: number;
}

export interface KfsKanbanData {
  columns: Record<string, KfsKanbanLead[]>;
  stats: KfsKanbanColumnStat[];
  total: number;
  filters: {
    cities: string[];
    assignees: string[];
  };
}

export interface KfsKanbanActivity {
  id: number;
  lead_id: number;
  action: string;
  details: string | null;
  performed_by: string | null;
  created_at: string;
}

export interface KfsKanbanLeadDetail {
  lead: KfsKanbanLead & Record<string, any>;
  activity: KfsKanbanActivity[];
  matchedSessions: Array<{
    id: number;
    title: string;
    date_start: string;
    date_end: string;
    status: string;
    location: string | null;
    company_name: string | null;
  }>;
}

// === KFS Import types ===

export interface KfsImportPreviewRow {
  company_name?: string;
  nip?: string;
  regon?: string;
  email?: string;
  phone?: string;
  company_city?: string;
  company_zip?: string;
  company_address?: string;
  pkd_main?: string;
  first_name?: string;
  last_name?: string;
  notes?: string;
  training_area?: string;
  powiat?: string;
  voivodeship?: string;
  company_size?: string;
  employee_count?: string;
  website_url?: string;
  row_number: number;
  errors: string[];
  is_valid: boolean;
  is_duplicate: boolean;
  existing_id?: number;
  duplicate_reason?: string;
}

export interface KfsImportPreviewResult {
  headers: string[];
  total_rows: number;
  valid: number;
  duplicates: number;
  errors: number;
  preview: KfsImportPreviewRow[];
  all_rows: KfsImportPreviewRow[];
  auto_mapping: Record<number, string>;
  delimiter_detected: string;
}

export interface KfsImportExecuteResult {
  imported: number;
  updated: number;
  skipped: number;
  errors: number;
  total: number;
  batch: string;
}

export interface KfsImportHistoryEntry {
  import_batch: string;
  source_detail: string;
  data_source: string;
  count: number;
  import_date: string;
  cities: number;
  with_nip: number;
  with_email: number;
}

// ─── KFS Automation Rules Engine Types ─────────────────────────

export type KfsConditionField =
  | 'score' | 'city' | 'pkd_main' | 'status' | 'source' | 'digital_maturity_score'
  | 'employee_estimate' | 'employee_count' | 'company_size' | 'voivodeship' | 'powiat'
  | 'days_since_created' | 'days_since_contact' | 'contact_count'
  | 'has_email' | 'has_phone' | 'has_nip' | 'training_area' | 'data_source'
  | 'has_disability' | 'is_over_50' | 'low_qualifications' | 'has_large_family_card';

export type KfsConditionOperator =
  | 'equals' | 'not_equals' | 'greater_than' | 'less_than'
  | 'greater_or_equal' | 'less_or_equal'
  | 'contains' | 'not_contains' | 'starts_with'
  | 'in' | 'not_in'
  | 'is_null' | 'is_not_null';

export type KfsActionType =
  | 'change_status' | 'assign_to' | 'add_tag' | 'remove_tag'
  | 'schedule_followup' | 'update_score' | 'set_priority'
  | 'move_to_nabor' | 'flag_for_review' | 'add_note';

export type KfsTriggerType = 'on_create' | 'on_update' | 'on_status_change' | 'scheduled' | 'manual';

export interface KfsRuleCondition {
  field: KfsConditionField;
  operator: KfsConditionOperator;
  value: any;
}

export interface KfsRuleAction {
  type: KfsActionType;
  value: any;
}

export interface KfsAutomationRule {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  priority: number;
  trigger: KfsTriggerType;
  conditions: KfsRuleCondition[];
  actions: KfsRuleAction[];
  run_count: number;
  last_run_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface KfsAutomationLogEntry {
  id: number;
  rule_id: number;
  rule_name?: string;
  lead_id: number;
  lead_name?: string;
  actions_executed: string[];
  created_at: string;
}

export interface KfsAutomationStats {
  total_rules: number;
  active_rules: number;
  total_runs: number;
  runs_today: number;
  top_rules: Array<{ name: string; run_count: number; last_run: string | null }>;
  recent_log: Array<{ rule_name: string; lead_name: string; actions: string[]; when: string }>;
}

export interface KfsAutomationMetadata {
  condition_fields: Array<{ value: string; label: string; type: string; options?: string[] }>;
  operators: Array<{ value: string; label: string; types: string[] }>;
  action_types: Array<{ value: string; label: string; valueType: string; options?: string[] }>;
  trigger_types: Array<{ value: string; label: string }>;
}

export interface KfsRunRulesResult {
  rules_checked: number;
  rules_matched: number;
  actions_executed: string[];
}

export interface KfsScheduledRunResult {
  dry_run: boolean;
  rules_processed: number;
  leads_affected: number;
  actions_total: number;
  details: Array<{ rule_name: string; leads_matched: number; actions: string[] }>;
}

// ─── KFS Automation Rules API ──────────────────────────────────

export const kfsAutomationApi = {
  // Metadata for UI rule builder
  async getMetadata(): Promise<KfsAutomationMetadata> {
    const { data } = await api.get('/kfs/automation/metadata');
    return data;
  },

  // Statistics
  async getStats(): Promise<KfsAutomationStats> {
    const { data } = await api.get('/kfs/automation/stats');
    return data;
  },

  // Automation log
  async getLog(params?: { ruleId?: number; leadId?: number; limit?: number; offset?: number }): Promise<{ entries: KfsAutomationLogEntry[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.ruleId) searchParams.set('ruleId', String(params.ruleId));
    if (params?.leadId) searchParams.set('leadId', String(params.leadId));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const { data } = await api.get(`/kfs/automation/log?${searchParams}`);
    return data;
  },

  // CRUD Rules
  async getRules(activeOnly?: boolean): Promise<{ rules: KfsAutomationRule[]; total: number }> {
    const params = activeOnly ? '?active=true' : '';
    const { data } = await api.get(`/kfs/automation/rules${params}`);
    return data;
  },

  async getRule(id: number): Promise<KfsAutomationRule> {
    const { data } = await api.get(`/kfs/automation/rules/${id}`);
    return data;
  },

  async createRule(rule: Partial<KfsAutomationRule>): Promise<KfsAutomationRule> {
    const { data } = await api.post('/kfs/automation/rules', rule);
    return data;
  },

  async updateRule(id: number, updates: Partial<KfsAutomationRule>): Promise<KfsAutomationRule> {
    const { data } = await api.put(`/kfs/automation/rules/${id}`, updates);
    return data;
  },

  async deleteRule(id: number): Promise<void> {
    await api.delete(`/kfs/automation/rules/${id}`);
  },

  async toggleRule(id: number, active: boolean): Promise<void> {
    await api.post(`/kfs/automation/rules/${id}/toggle`, { active });
  },

  // Execution
  async runScheduled(dryRun = false): Promise<KfsScheduledRunResult> {
    const { data } = await api.post('/kfs/automation/run-scheduled', { dry_run: dryRun });
    return data;
  },

  async runForLead(leadId: number, trigger: KfsTriggerType = 'manual'): Promise<KfsRunRulesResult> {
    const { data } = await api.post(`/kfs/automation/run-for-lead/${leadId}`, { trigger });
    return data;
  },

  // Preview: how many leads match given conditions
  async previewConditions(conditions: KfsRuleCondition[]): Promise<{ matched: number; sample: Array<{ id: number; name: string; company: string }> }> {
    const { data } = await api.post('/kfs/automation/preview-conditions', { conditions });
    return data;
  },

  // Seed default rules
  async seedDefaults(): Promise<{ seeded: number }> {
    const { data } = await api.post('/kfs/automation/seed');
    return data;
  },

  // Search leads for test picker
  async searchLeads(q: string): Promise<Array<{ id: number; name: string; company: string }>> {
    const { data } = await api.get(`/kfs/automation/search-leads?q=${encodeURIComponent(q)}`);
    return data;
  },
};

// ─── KFS Performance Tracker API ────────────────────────────────────

export interface KfsTeamMember {
  id: number;
  username: string;
  full_name: string;
  leads_period: string;
  total_leads: string;
  contacted: string;
  interested: string;
  converted: string;
  active_last_week: string;
  avg_score: string | null;
  cities_covered: string;
  rank: number;
  activity_count: number;
  conversion_rate: string;
}

export interface KfsTeamTotals {
  total_leads: number;
  total_converted: number;
  total_activities: number;
  avg_conversion: string;
}

export interface KfsTeamPerformance {
  team: KfsTeamMember[];
  totals: KfsTeamTotals;
  period: number;
}

export interface KfsIndividualStats {
  total_leads: string;
  new_leads: string;
  contacted: string;
  interested: string;
  offers: string;
  converted: string;
  rejected: string;
  avg_score: string | null;
  cities: string;
  overdue: string;
}

export interface KfsMonthlyTrend {
  month: string;
  leads: string;
  converted: string;
}

export interface KfsCityDistribution {
  city: string;
  count: string;
}

export interface KfsPerformanceGoals {
  monthly_lead_goal: number;
  monthly_conversion_goal: number;
  monthly_activity_goal: number;
}

export interface KfsIndividualPerformance {
  stats: KfsIndividualStats;
  trend: KfsMonthlyTrend[];
  activities: any[];
  cities: KfsCityDistribution[];
  goals: KfsPerformanceGoals;
}

export const kfsPerformanceApi = {
  async getTeamPerformance(period: number = 30): Promise<KfsTeamPerformance> {
    const { data } = await api.get(`/kfs/performance/team?period=${period}`);
    return data;
  },

  async getIndividualPerformance(userId: number): Promise<KfsIndividualPerformance> {
    const { data } = await api.get(`/kfs/performance/individual/${userId}`);
    return data;
  },

  async setGoals(userId: number, goals: Partial<KfsPerformanceGoals>): Promise<void> {
    await api.post('/kfs/performance/goals', {
      userId,
      monthlyLeadGoal: goals.monthly_lead_goal,
      monthlyConversionGoal: goals.monthly_conversion_goal,
      monthlyActivityGoal: goals.monthly_activity_goal,
    });
  },

  async getLeaderboardHistory(weeks: number = 8): Promise<any[]> {
    const { data } = await api.get(`/kfs/performance/leaderboard-history?weeks=${weeks}`);
    return data;
  },
};
